import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Home, 
  Users, 
  BookOpen, 
  DollarSign, 
  Package, 
  Tag, 
  Settings, 
  BarChart3, 
  FileText, 
  Shield, 
  Bell, 
  Activity,
  Crown,
  HardDrive,
  Flag,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  AlertCircle,
  ArrowRight,
  Eye,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  Clock,
  FileBarChart,
  Ban,
  RefreshCw,
  EyeOff,
  Search,
  Handshake,
  Star,
  Plus,
  Minus,
  ExternalLink,
  CheckSquare,
  Square,
  ChevronDown,
  ChevronUp,
  MoreVertical,
  Wrench,
  Power,
  PowerOff,
  Save,
  UserX,
  Brain
} from 'lucide-react';
import { db, functions } from '../lib/firebase';
import VideoReportsAdmin from './VideoReportsAdmin';
import RevenueChart from './RevenueChart';
import TaxReportExport from './TaxReportExport';
import PaymentSettingsSection from './PaymentSettingsSection';
import AISystemAdmin from '../pages/admin/AISystemAdmin';
import AdminSettings from './AdminSettings';
import { collection, query, orderBy, getDocs, doc, updateDoc, deleteDoc, where, getDoc, setDoc, limit } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useAuthContext } from '../contexts/AuthContext';

interface AdminContentProps {
  activeSection: string;
  stats: any;
  users: any[];
  topStorageUsers: any[];
  recentLogs: any[];
  onSectionChange?: (section: string) => void;
  pendingApprovals?: {
    pendingTrials: number;
    pendingPayments: number;
  };
}

export default function AdminContent({
  activeSection,
  stats,
  users,
  topStorageUsers,
  recentLogs,
  onSectionChange,
  pendingApprovals
}: AdminContentProps) {
  const navigate = useNavigate();
  const [reportedCourses, setReportedCourses] = useState<any[]>([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [instructorSearchQuery, setInstructorSearchQuery] = useState('');
  const [partners, setPartners] = useState<any[]>([]);
  const [loadingPartners, setLoadingPartners] = useState(false);
  const [courses, setCourses] = useState<any[]>([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [subscriptionPayments, setSubscriptionPayments] = useState<any[]>([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [pendingPayments, setPendingPayments] = useState<any[]>([]);
  const [loadingPendingPayments, setLoadingPendingPayments] = useState(false);
  const [discountCodes, setDiscountCodes] = useState<any[]>([]);
  const [loadingDiscounts, setLoadingDiscounts] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [discountForm, setDiscountForm] = useState({
    code: '',
    type: 'percentage',
    value: 0,
    maxUses: 0,
    expiresAt: '',
    description: ''
  });
  const [storageStats, setStorageStats] = useState<any>(null);
  const [loadingStorage, setLoadingStorage] = useState(false);
  const [showFileList, setShowFileList] = useState(false);
  const [showTopUsers, setShowTopUsers] = useState(false);
  const [cleanupResult, setCleanupResult] = useState<any>(null);
  const [loadingCleanup, setLoadingCleanup] = useState(false);
  const [showCleanupModal, setShowCleanupModal] = useState(false);
  const [recalculateResult, setRecalculateResult] = useState<any>(null);
  const [loadingRecalculate, setLoadingRecalculate] = useState(false);
  const [showRecalculateModal, setShowRecalculateModal] = useState(false);
  const [showAllFiles, setShowAllFiles] = useState(false);
  const [allFiles, setAllFiles] = useState<any[]>([]);
  const [loadingAllFiles, setLoadingAllFiles] = useState(false);
  const [fileSearchQuery, setFileSearchQuery] = useState('');
  const [totalFilesCount, setTotalFilesCount] = useState(0);
  const [totalFilesSize, setTotalFilesSize] = useState(0);
  const [selectedSearchFiles, setSelectedSearchFiles] = useState<Set<string>>(new Set());
  const [deletingBulkSearch, setDeletingBulkSearch] = useState(false);
  const [showInactiveFiles, setShowInactiveFiles] = useState(false);
  const [inactiveFiles, setInactiveFiles] = useState<any[]>([]);
  const [loadingInactiveFiles, setLoadingInactiveFiles] = useState(false);
  const [inactiveThreshold, setInactiveThreshold] = useState(1);
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [deletingBulk, setDeletingBulk] = useState(false);
  const [showMarkedFiles, setShowMarkedFiles] = useState(false);
  const [markedFiles, setMarkedFiles] = useState<any[]>([]);
  const [loadingMarkedFiles, setLoadingMarkedFiles] = useState(false);
  const [restoringFile, setRestoringFile] = useState<string | null>(null);
  const [deletingMarkedFile, setDeletingMarkedFile] = useState<string | null>(null);
  const [showStorageActionsMenu, setShowStorageActionsMenu] = useState(false);
  const [showReuploadModal, setShowReuploadModal] = useState(false);
  const [reuploadFile, setReuploadFile] = useState<any>(null);
  const [uploadingReplacement, setUploadingReplacement] = useState(false);
  
  // Activity Logs states
  const [activityLogs, setActivityLogs] = useState<any[]>([]);
  const [loadingActivityLogs, setLoadingActivityLogs] = useState(false);
  
  // Notifications states
  const [notifications, setNotifications] = useState<any[]>([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  
  // Payment Settings states
  const [paymentSettings, setPaymentSettings] = useState<any>({
    qrCodeUrl: '',
    bankName: '',
    accountName: '',
    accountNumber: '',
    promptPayNumber: ''
  });
  const [loadingPaymentSettings, setLoadingPaymentSettings] = useState(false);
  const [savingPaymentSettings, setSavingPaymentSettings] = useState(false);
  const [qrFile, setQrFile] = useState<File | null>(null);
  const [qrPreview, setQrPreview] = useState<string>('');
  
  // Analytics states
  const [analyticsData, setAnalyticsData] = useState<any>({
    newUsers7Days: 0,
    newCourses7Days: 0,
    visitsToday: 0,
    growthRate: 0
  });
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  
  // Usage Stats states
  const [usageStats, setUsageStats] = useState<any>({
    activeUsers: 0,
    activeCourses: 0
  });
  const [loadingUsageStats, setLoadingUsageStats] = useState(false);
  
  // System Health states
  const [systemHealth, setSystemHealth] = useState<any>({
    firebase: { status: 'operational', message: 'ทำงานปกติ' },
    cloudflareR2: { status: 'operational', message: 'ทำงานปกติ' },
    authentication: { status: 'operational', message: 'ทำงานปกติ' },
    database: { status: 'operational', message: 'ทำงานปกติ' }
  });
  const [loadingSystemHealth, setLoadingSystemHealth] = useState(false);
  
  // Delete User states
  const [deletingUser, setDeletingUser] = useState<string | null>(null);
  
  // Pending Payments Count for Overview
  const [pendingPaymentsCount, setPendingPaymentsCount] = useState(0);
  const [loadingPendingCount, setLoadingPendingCount] = useState(false);
  
  // Maintenance Mode states
  const { user } = useAuthContext();
  const [maintenanceEnabled, setMaintenanceEnabled] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState("ขณะนี้ระบบ EduMi อยู่ระหว่างปรับปรุงชั่วคราว\nทีมงานกำลังอัปเดตระบบ กรุณากลับมาใหม่อีกครั้ง");
  const [loadingMaintenance, setLoadingMaintenance] = useState(false);
  const [savingMaintenance, setSavingMaintenance] = useState(false);
  const [maintenanceSuccessMessage, setMaintenanceSuccessMessage] = useState("");

  useEffect(() => {
    if (activeSection === 'overview') {
      loadPendingPaymentsCount();
    }
    if (activeSection === 'flagged-courses') {
      loadReportedCourses();
    }
    if (activeSection === 'partners') {
      loadPartners();
    }
    if (activeSection === 'all-courses') {
      loadCourses();
    }
    if (activeSection === 'subscription-payments') {
      loadSubscriptionPayments();
    }
    if (activeSection === 'payment-approval') {
      loadPendingPayments();
    }
    if (activeSection === 'discount-management') {
      loadDiscountCodes();
    }
    if (activeSection === 'storage-management') {
      loadStorageStats();
    }
    if (activeSection === 'maintenance-mode') {
      loadMaintenanceSettings();
    }
    if (activeSection === 'activity-logs') {
      loadActivityLogs();
    }
    if (activeSection === 'notifications') {
      loadNotifications();
    }
    if (activeSection === 'analytics') {
      loadAnalytics();
    }
    if (activeSection === 'usage-stats') {
      loadUsageStats();
    }
    if (activeSection === 'system-health') {
      loadSystemHealth();
    }
  }, [activeSection]);

  const loadReportedCourses = async () => {
    setLoadingReports(true);
    try {
      const q = query(
        collection(db, 'courseReports'),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      const reports = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setReportedCourses(reports);
    } catch (error) {
      console.error('Error loading reports:', error);
    } finally {
      setLoadingReports(false);
    }
  };

  const loadDiscountCodes = async () => {
    setLoadingDiscounts(true);
    try {
      const q = query(
        collection(db, 'discountCodes'),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      const codes = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setDiscountCodes(codes);
    } catch (error) {
      console.error('Error loading discount codes:', error);
    } finally {
      setLoadingDiscounts(false);
    }
  };

  const loadPartners = async () => {
    setLoadingPartners(true);
    try {
      const snapshot = await getDocs(collection(db, 'featuredPartners'));
      const partnersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      partnersData.sort((a: any, b: any) => a.order - b.order);
      setPartners(partnersData);
    } catch (error) {
      console.error('Error loading partners:', error);
    } finally {
      setLoadingPartners(false);
    }
  };

  const loadCourses = async () => {
    setLoadingCourses(true);
    try {
      const q = query(
        collection(db, 'courses'),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      const coursesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCourses(coursesData);
    } catch (error) {
      console.error('Error loading courses:', error);
    } finally {
      setLoadingCourses(false);
    }
  };

  const handleDeleteUser = async (userId: string, userEmail: string, userName: string) => {
    const confirmMessage = `⚠️ คำเตือน: การลบผู้ใช้จะลบข้อมูลทั้งหมดอย่างถาวร!\n\nผู้ใช้: ${userName || 'ไม่ระบุชื่อ'}\nEmail: ${userEmail}\n\nข้อมูลที่จะถูกลบ:\n• บัญชีผู้ใช้\n• คอร์สทั้งหมด\n• บทเรียนทั้งหมด\n• ความคืบหน้าการเรียน\n• การชำระเงิน\n• ไฟล์สื่อทั้งหมด\n• ข้อมูลอื่นๆ ทั้งหมด\n\nผู้ใช้จะต้องสมัครใหม่เท่านั้น\n\nพิมพ์ "DELETE" เพื่อยืนยัน`;
    
    const userInput = prompt(confirmMessage);
    
    if (userInput !== 'DELETE') {
      if (userInput !== null) {
        alert('ยกเลิกการลบผู้ใช้');
      }
      return;
    }

    setDeletingUser(userId);
    
    try {
      const deleteUserFn = httpsCallable(functions, 'deleteUser');
      const result = await deleteUserFn({ userId });
      const data = result.data as any;
      
      if (data.success) {
        alert(`✅ ${data.message}\n\nลบเอกสารทั้งหมด: ${data.deletedDocuments} รายการ`);
        window.location.reload();
      } else {
        alert('เกิดข้อผิดพลาดในการลบผู้ใช้');
      }
    } catch (error: any) {
      console.error('Error deleting user:', error);
      alert(`เกิดข้อผิดพลาด: ${error.message || 'ไม่สามารถลบผู้ใช้ได้'}`);
    } finally {
      setDeletingUser(null);
    }
  };

  const handleReportAction = async (reportId: string, action: 'approve' | 'reject' | 'delete') => {
    try {
      const reportRef = doc(db, 'courseReports', reportId);
      
      if (action === 'delete') {
        await deleteDoc(reportRef);
      } else {
        await updateDoc(reportRef, {
          status: action === 'approve' ? 'approved' : 'rejected',
          reviewedAt: new Date(),
        });
      }
      
      await loadReportedCourses();
    } catch (error) {
      console.error('Error handling report:', error);
      alert('เกิดข้อผิดพลาดในการดำเนินการ');
    }
  };

  const loadMaintenanceSettings = async () => {
    setLoadingMaintenance(true);
    try {
      const docRef = doc(db, "system", "maintenance");
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        setMaintenanceEnabled(data.enabled || false);
        setMaintenanceMessage(data.message || "ขณะนี้ระบบ EduMi อยู่ระหว่างปรับปรุงชั่วคราว\nทีมงานกำลังอัปเดตระบบ กรุณากลับมาใหม่อีกครั้ง");
      }
    } catch (error) {
      console.error("Error loading maintenance settings:", error);
    } finally {
      setLoadingMaintenance(false);
    }
  };

  const handleSaveMaintenance = async () => {
    if (!user) return;
    
    setSavingMaintenance(true);
    setMaintenanceSuccessMessage("");
    
    try {
      const docRef = doc(db, "system", "maintenance");
      await setDoc(docRef, {
        enabled: maintenanceEnabled,
        message: maintenanceMessage,
        updatedAt: new Date(),
        updatedBy: user.email,
      });
      
      setMaintenanceSuccessMessage("บันทึกการตั้งค่าสำเร็จ!");
      setTimeout(() => setMaintenanceSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error saving maintenance settings:", error);
      alert("เกิดข้อผิดพลาดในการบันทึก");
    } finally {
      setSavingMaintenance(false);
    }
  };

  const toggleMaintenance = async () => {
    const newState = !maintenanceEnabled;
    setMaintenanceEnabled(newState);
    
    if (!user) return;
    
    try {
      const docRef = doc(db, "system", "maintenance");
      await setDoc(docRef, {
        enabled: newState,
        message: maintenanceMessage,
        updatedAt: new Date(),
        updatedBy: user.email,
      });
      
      setMaintenanceSuccessMessage(newState ? "เปิดโหมดปรับปรุงแล้ว!" : "ปิดโหมดปรับปรุงแล้ว!");
      setTimeout(() => setMaintenanceSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error toggling maintenance:", error);
      setMaintenanceEnabled(!newState);
      alert("เกิดข้อผิดพลาดในการเปลี่ยนสถานะ");
    }
  };

  const loadSubscriptionPayments = async () => {
    setLoadingPayments(true);
    try {
      const q = query(
        collection(db, 'subscriptionPayments'),
        where('status', '==', 'completed'),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      const payments = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || null,
        };
      });
      setSubscriptionPayments(payments);
    } catch (error) {
      console.error('Error loading subscription payments:', error);
    } finally {
      setLoadingPayments(false);
    }
  };

  const loadPendingPaymentsCount = async () => {
    setLoadingPendingCount(true);
    try {
      // Count subscription payments
      const subscriptionQuery = query(
        collection(db, 'subscriptionPayments'),
        where('status', '==', 'pending')
      );
      const subscriptionSnapshot = await getDocs(subscriptionQuery);
      
      let addonCount = 0;
      try {
        // Count addon payments (may fail if index doesn't exist)
        const addonQuery = query(
          collection(db, 'addonPayments'),
          where('status', '==', 'pending')
        );
        const addonSnapshot = await getDocs(addonQuery);
        addonCount = addonSnapshot.size;
      } catch (addonError) {
        console.warn('Could not load addon payments count (index may not exist):', addonError);
      }
      
      const totalPending = subscriptionSnapshot.size + addonCount;
      setPendingPaymentsCount(totalPending);
    } catch (error) {
      console.error('Error loading pending payments count:', error);
    } finally {
      setLoadingPendingCount(false);
    }
  };

  const loadPendingPayments = async () => {
    console.log('🔍 Starting loadPendingPayments...');
    setLoadingPendingPayments(true);
    try {
      // First, check if collection exists and has any data
      console.log('📋 Checking subscriptionPayments collection...');
      const allSubscriptionsSnapshot = await getDocs(collection(db, 'subscriptionPayments'));
      console.log('📊 Total subscriptionPayments documents:', allSubscriptionsSnapshot.size);
      
      if (allSubscriptionsSnapshot.size > 0) {
        console.log('📄 All subscription payments:');
        allSubscriptionsSnapshot.docs.forEach(doc => {
          console.log('  -', doc.id, ':', doc.data());
        });
      }
      
      // Load subscription payments - WITHOUT orderBy to avoid index requirement
      console.log('📋 Loading PENDING subscription payments...');
      const subscriptionQuery = query(
        collection(db, 'subscriptionPayments'),
        where('status', '==', 'pending')
      );
      
      const subscriptionSnapshot = await getDocs(subscriptionQuery);
      console.log('✅ Pending subscription payments loaded:', subscriptionSnapshot.size);
      
      const subscriptionPayments = subscriptionSnapshot.docs.map(doc => {
        const data = doc.data();
        console.log('📄 Pending subscription payment:', doc.id, data);
        return {
          id: doc.id,
          type: 'subscription',
          ...data,
          createdAt: data.createdAt?.toDate() || null,
        };
      });

      let addonPayments = [];
      try {
        // Load addon payments (may fail if index doesn't exist)
        console.log('📋 Loading addon payments...');
        const addonQuery = query(
          collection(db, 'addonPayments'),
          where('status', '==', 'pending')
        );
        const addonSnapshot = await getDocs(addonQuery);
        console.log('✅ Addon payments loaded:', addonSnapshot.size);
        
        addonPayments = addonSnapshot.docs.map(doc => {
          const data = doc.data();
          console.log('📄 Addon payment:', doc.id, data);
          return {
            id: doc.id,
            type: 'addon',
            ...data,
            createdAt: data.createdAt?.toDate() || null,
          };
        });
      } catch (addonError) {
        console.warn('⚠️ Could not load addon payments:', addonError);
      }

      // Combine and sort by date
      const allPayments = [...subscriptionPayments, ...addonPayments].sort((a, b) => {
        if (!a.createdAt) return 1;
        if (!b.createdAt) return -1;
        return b.createdAt.getTime() - a.createdAt.getTime();
      });

      console.log('✅ Total pending payments:', allPayments.length);
      console.log('📊 All payments:', allPayments);
      setPendingPayments(allPayments);
    } catch (error) {
      console.error('❌ Error loading pending payments:', error);
      alert('เกิดข้อผิดพลาดในการโหลดข้อมูล: ' + (error as Error).message);
    } finally {
      setLoadingPendingPayments(false);
      console.log('🏁 loadPendingPayments finished');
    }
  };

  const handlePaymentAction = async (paymentId: string, action: 'approve' | 'reject', paymentType?: string) => {
    try {
      // Find the payment to determine its type
      const payment = pendingPayments.find(p => p.id === paymentId);
      if (!payment) {
        alert('ไม่พบข้อมูลการชำระเงิน');
        return;
      }

      const collectionName = payment?.type === 'addon' ? 'addonPayments' : 'subscriptionPayments';
      
      // Update payment status
      const paymentRef = doc(db, collectionName, paymentId);
      await updateDoc(paymentRef, {
        status: action === 'approve' ? 'completed' : 'rejected',
        reviewedAt: new Date(),
      });
      
      // If approved, update user data
      if (action === 'approve') {
        const userEmail = payment.instructorEmail || payment.email;
        if (userEmail) {
          // Find user by email
          const usersQuery = query(
            collection(db, 'users'),
            where('email', '==', userEmail)
          );
          const usersSnapshot = await getDocs(usersQuery);
          
          if (!usersSnapshot.empty) {
            const userDoc = usersSnapshot.docs[0];
            const userRef = doc(db, 'users', userDoc.id);
            
            if (payment.type === 'addon') {
              // Update storage quota for addon
              const storageAmount = payment.storageAmount || 0;
              const currentQuota = userDoc.data()?.storageQuota || 0;
              
              await updateDoc(userRef, {
                storageQuota: currentQuota + (storageAmount * 1024 * 1024 * 1024), // Convert GB to bytes
                updatedAt: new Date()
              });
              
              alert(`อนุมัติการชำระเงินเรียบร้อย\nเพิ่มพื้นที่ ${storageAmount} GB ให้ ${userEmail} แล้ว`);
            } else {
              // Update subscription tier
              const plan = payment.plan || 'pro';
              await updateDoc(userRef, {
                subscriptionTier: plan,
                subscriptionStatus: 'active',
                subscriptionStartDate: new Date(),
                updatedAt: new Date()
              });
              
              alert(`อนุมัติการชำระเงินเรียบร้อย\nอัปเดตแพ็กเกจเป็น ${plan.toUpperCase()} ให้ ${userEmail} แล้ว`);
            }
          } else {
            alert('ไม่พบผู้ใช้ในระบบ กรุณาตรวจสอบอีเมล');
          }
        } else {
          alert('ไม่พบข้อมูลอีเมลผู้ใช้');
        }
      } else {
        alert('ปฏิเสธการชำระเงินเรียบร้อย');
      }
      
      await loadPendingPayments();
      await loadPendingPaymentsCount(); // Refresh count
    } catch (error) {
      console.error('Error handling payment:', error);
      alert('เกิดข้อผิดพลาดในการดำเนินการ: ' + (error as Error).message);
    }
  };

  const safeStats = {
    totalUsers: Number(stats?.totalUsers ?? 0),
    newUsersThisMonth: Number(stats?.newUsersThisMonth ?? 0),
    totalRevenue: Number(stats?.totalRevenue ?? 0),
    growthRate: Number(stats?.growthRate ?? 0),
    totalCourses: Number(stats?.totalCourses ?? 0),
    activeCourses: Number(stats?.activeCourses ?? 0),
    activeSubscriptions: Number(stats?.activeSubscriptions ?? 0),
    revenueThisMonth: Number(stats?.revenueThisMonth ?? 0),
    revenueLastMonth: Number(stats?.revenueLastMonth ?? 0),
  };
  
  const renderOverview = () => (
    <div className="space-y-6">
      {/* Pending Approvals Card from Salepage System */}
      {(pendingApprovals && (pendingApprovals.pendingTrials > 0 || pendingApprovals.pendingPayments > 0)) && (
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-400 rounded-2xl shadow-lg p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-yellow-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <Clock className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">มีรายการรอการอนุมัติ</h3>
                <div className="space-y-1 mb-4">
                  {pendingApprovals.pendingTrials > 0 && (
                    <p className="text-gray-700 flex items-center gap-2">
                      <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                      <span className="font-semibold text-yellow-700">{pendingApprovals.pendingTrials}</span> คำขอทดลองใช้ฟรี 7 วัน
                    </p>
                  )}
                  {pendingApprovals.pendingPayments > 0 && (
                    <p className="text-gray-700 flex items-center gap-2">
                      <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                      <span className="font-semibold text-orange-700">{pendingApprovals.pendingPayments}</span> การชำระเงินรอตรวจสอบ
                    </p>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={() => navigate('/admin/payment-approval')}
              className="flex items-center gap-2 px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-white rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <CheckCircle className="w-5 h-5" />
              <span>ไปอนุมัติ</span>
            </button>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {/* Users Card */}
        <div className="bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl p-4 sm:p-6 text-white shadow-large hover:shadow-xl transition-all duration-300 transform hover:scale-105">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
              <Users className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
            </div>
            <span className="text-xs sm:text-sm bg-white/20 px-2 sm:px-3 py-1 rounded-full backdrop-blur-sm">
              Total
            </span>
          </div>
          <p className="text-xs sm:text-sm opacity-90 mb-1 sm:mb-2">ผู้ใช้ทั้งหมด</p>
          <p className="text-2xl sm:text-3xl lg:text-4xl font-bold">{safeStats.totalUsers}</p>
          <p className="text-xs opacity-75 mt-2">+{safeStats.newUsersThisMonth} เดือนนี้</p>
        </div>

        {/* Revenue Card */}
        <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-4 sm:p-6 text-white shadow-large hover:shadow-xl transition-all duration-300 transform hover:scale-105">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
              <DollarSign className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
            </div>
            <span className="text-xs sm:text-sm bg-white/20 px-2 sm:px-3 py-1 rounded-full backdrop-blur-sm">
              +{safeStats.growthRate.toFixed(1)}%
            </span>
          </div>
          <p className="text-xs sm:text-sm opacity-90 mb-1 sm:mb-2">รายได้รวม</p>
          <p className="text-2xl sm:text-3xl lg:text-4xl font-bold">฿{safeStats.totalRevenue.toLocaleString()}</p>
          <p className="text-xs opacity-75 mt-2">เติบโตต่อเนื่อง</p>
        </div>

        {/* Courses Card */}
        <div className="bg-gradient-to-br from-blue-500 to-pink-600 rounded-2xl p-4 sm:p-6 text-white shadow-large hover:shadow-xl transition-all duration-300 transform hover:scale-105">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
              <BookOpen className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
            </div>
            <span className="text-xs sm:text-sm bg-white/20 px-2 sm:px-3 py-1 rounded-full backdrop-blur-sm">
              Active
            </span>
          </div>
          <p className="text-xs sm:text-sm opacity-90 mb-1 sm:mb-2">คอร์สทั้งหมด</p>
          <p className="text-2xl sm:text-3xl lg:text-4xl font-bold">{safeStats.totalCourses}</p>
          <p className="text-xs opacity-75 mt-2">{safeStats.activeCourses} ใช้งาน</p>
        </div>

        {/* Subscriptions Card */}
        <div className="bg-gradient-to-br from-blue-500 to-red-600 rounded-2xl p-4 sm:p-6 text-white shadow-large hover:shadow-xl transition-all duration-300 transform hover:scale-105">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
              <Crown className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
            </div>
            <span className="text-xs sm:text-sm bg-white/20 px-2 sm:px-3 py-1 rounded-full backdrop-blur-sm">
              Premium
            </span>
          </div>
          <p className="text-xs sm:text-sm opacity-90 mb-1 sm:mb-2">สมาชิกที่ active</p>
          <p className="text-2xl sm:text-3xl lg:text-4xl font-bold">{safeStats.activeSubscriptions}</p>
          <p className="text-xs opacity-75 mt-2">แพ็กเกจพรีเมียม</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Manage Users Card */}
        <button onClick={() => onSectionChange?.('all-users')} className="block text-left w-full touch-target">
          <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 border-2 border-blue-100 hover:border-blue-300">
            <div className="flex items-start justify-between mb-3 sm:mb-4">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl sm:rounded-2xl flex items-center justify-center">
                <Users className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </div>
              <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-textDark mb-2">จัดการผู้ใช้</h3>
            <p className="text-sm sm:text-base text-textLight mb-3 sm:mb-4">ดูและจัดการผู้ใช้ทั้งหมดในระบบ</p>
            <div className="flex items-center gap-2">
              <span className="text-2xl sm:text-3xl font-bold text-primary">{safeStats.totalUsers}</span>
              <span className="text-sm sm:text-base text-textLight">ผู้ใช้</span>
            </div>
          </div>
        </button>

        {/* Manage Courses Card */}
        <button onClick={() => onSectionChange?.('all-courses')} className="block text-left w-full touch-target">
          <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 border-2 border-blue-100 hover:border-blue-300">
            <div className="flex items-start justify-between mb-3 sm:mb-4">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-blue-500 to-pink-600 rounded-xl sm:rounded-2xl flex items-center justify-center">
                <BookOpen className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </div>
              <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-textDark mb-2">จัดการคอร์ส</h3>
            <p className="text-sm sm:text-base text-textLight mb-3 sm:mb-4">ตรวจสอบและจัดการคอร์สทั้งหมด</p>
            <div className="text-sm text-blue-600 font-semibold">ดูคอร์สทั้งหมด →</div>
          </div>
        </button>

        {/* Revenue Card */}
        <button onClick={() => onSectionChange?.('total-revenue')} className="block text-left w-full touch-target">
          <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 border-2 border-green-100 hover:border-green-300">
            <div className="flex items-start justify-between mb-3 sm:mb-4">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl sm:rounded-2xl flex items-center justify-center">
                <DollarSign className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </div>
              <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-textDark mb-2">รายได้</h3>
            <p className="text-sm sm:text-base text-textLight mb-3 sm:mb-4">ดูรายงานและสถิติรายได้</p>
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-green-600" />
              <span className="text-sm text-green-600 font-semibold">ดูข้อมูล →</span>
            </div>
          </div>
        </button>

        {/* AI System Card */}
        <button onClick={() => onSectionChange?.('ai-system')} className="block text-left w-full touch-target">
          <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 border-2 border-purple-100 hover:border-purple-300">
            <div className="flex items-start justify-between mb-3 sm:mb-4">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl sm:rounded-2xl flex items-center justify-center">
                <Brain className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </div>
              <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-textDark mb-2">AI System</h3>
            <p className="text-sm sm:text-base text-textLight mb-3 sm:mb-4">จัดการคอร์ส AI และการชำระเงิน</p>
            <div className="text-sm text-purple-600 font-semibold">จัดการระบบ →</div>
          </div>
        </button>
      </div>
    </div>
  );

  const renderAllUsers = () => (
    <div className="bg-white rounded-2xl shadow-xl">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center">
            <Users className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-textDark">จัดการผู้สอน</h2>
            <p className="text-sm text-textLight">จัดการผู้สอนในระบบ</p>
          </div>
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ชื่อ</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">อีเมล</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">บทบาท</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">สถานะ</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">แพ็กเกจ</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">การดำเนินการ</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.slice(0, 10).map((user) => (
              <tr key={user.uid} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center mr-3">
                      <span className="text-xs font-medium text-gray-600">
                        {user.name?.[0] || user.email?.[0]?.toUpperCase()}
                      </span>
                    </div>
                    <div className="text-sm font-medium text-gray-900">{user.name || 'N/A'}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.email}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    user.role === 'admin' ? 'bg-blue-100 text-blue-800' :
                    user.role === 'instructor' ? 'bg-blue-100 text-blue-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {user.role === 'admin' ? 'แอดมิน' : user.role === 'instructor' ? 'ผู้สอน' : 'นักเรียน'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    user.banned ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                  }`}>
                    {user.banned ? 'ถูกแบน' : 'ปกติ'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {user.subscriptionTier || user.plan || 'free'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <button
                    onClick={() => handleDeleteUser(user.uid, user.email, user.name)}
                    disabled={deletingUser === user.uid || user.email === 'viitamint@gmail.com'}
                    className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg font-medium transition-all ${
                      user.email === 'viitamint@gmail.com'
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : deletingUser === user.uid
                        ? 'bg-gray-100 text-gray-400 cursor-wait'
                        : 'bg-red-100 text-red-700 hover:bg-red-200'
                    }`}
                    title={user.email === 'viitamint@gmail.com' ? 'ไม่สามารถลบ Admin ได้' : 'ลบผู้ใช้และข้อมูลทั้งหมด'}
                  >
                    {deletingUser === user.uid ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        <span>กำลังลบ...</span>
                      </>
                    ) : (
                      <>
                        <UserX className="w-4 h-4" />
                        <span>ลบ</span>
                      </>
                    )}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      <div className="p-6 border-t border-gray-200">
        <div className="flex flex-wrap gap-3">
          <Link to="/admin/users">
            <button 
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Users className="w-5 h-5" />
              จัดการผู้สอน
            </button>
          </Link>
          <Link to="/admin/system">
            <button 
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FileBarChart className="w-5 h-5" />
              รายงานทั้งหมด
            </button>
          </Link>
        </div>
      </div>
    </div>
  );

  const renderInstructors = () => {
    const instructors = users.filter(u => u.role === 'instructor' || u.role === 'admin');
    
    const filteredInstructors = instructors.filter(instructor => {
      if (!instructorSearchQuery.trim()) return true;
      
      const searchLower = instructorSearchQuery.toLowerCase();
      const name = (instructor.name || '').toLowerCase();
      const email = (instructor.email || '').toLowerCase();
      
      return name.includes(searchLower) || email.includes(searchLower);
    });

    return (
      <div className="bg-white rounded-2xl shadow-xl">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-pink-600 rounded-xl flex items-center justify-center">
                <Crown className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-textDark">ผู้สอน</h2>
                <p className="text-sm text-textLight">จัดการผู้สอนในระบบ ({filteredInstructors.length} คน)</p>
              </div>
            </div>
          </div>
          
          {/* Search Box */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="ค้นหาผู้สอน (ชื่อหรืออีเมล)..."
              value={instructorSearchQuery}
              onChange={(e) => setInstructorSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {instructorSearchQuery && (
              <button
                onClick={() => setInstructorSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
        
        <div className="p-6">
          {filteredInstructors.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">
                {instructorSearchQuery ? 'ไม่พบผู้สอนที่ค้นหา' : 'ยังไม่มีผู้สอนในระบบ'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredInstructors.map((instructor) => (
                <div key={instructor.uid} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-all">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-blue-600">
                        {instructor.name?.[0] || instructor.email?.[0]?.toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">{instructor.name || 'N/A'}</h3>
                      <p className="text-sm text-gray-500 truncate">{instructor.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full uppercase">
                      {instructor.subscriptionTier || instructor.plan || 'free'}
                    </span>
                    <Link to={`/admin/instructor/${instructor.uid}`}>
                      <button 
                        className="text-blue-600 hover:text-blue-800 transition-colors"
                        title="ดูรายละเอียด"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderStudents = () => (
    <div className="bg-white rounded-2xl shadow-xl">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
            <Users className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-textDark">นักเรียน</h2>
            <p className="text-sm text-textLight">จัดการนักเรียนในระบบ</p>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 p-6">
        {users.filter(u => u.role === 'student').slice(0, 8).map((student) => (
          <div key={student.uid} className="border border-gray-200 rounded-lg p-4 hover:border-green-300 transition-all">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-xs font-medium text-green-600">
                  {student.name?.[0] || student.email?.[0]?.toUpperCase()}
                </span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 text-sm">{student.name || 'N/A'}</h3>
                <p className="text-xs text-gray-500">{student.email}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderAllCourses = () => (
    <div className="bg-white rounded-2xl shadow-xl">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-pink-600 rounded-xl flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-textDark">คอร์สทั้งหมด</h2>
              <p className="text-sm text-textLight">จัดการคอร์สทั้งหมดในระบบ</p>
            </div>
          </div>
          <Link to="/admin/courses-management">
            <button className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-pink-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all">
              <ExternalLink className="w-5 h-5" />
              จัดการเต็มรูปแบบ
            </button>
          </Link>
        </div>
      </div>
      
      <div className="p-6">
        {loadingCourses ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-500">กำลังโหลดข้อมูล...</p>
          </div>
        ) : courses.length === 0 ? (
          <div className="text-center py-12">
            <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">ยังไม่มีคอร์สในระบบ</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {courses.slice(0, 6).map((course: any) => (
                <div key={course.id} className="border border-gray-200 rounded-xl p-4 hover:border-blue-300 hover:shadow-md transition-all">
                  {course.thumbnail ? (
                    <img
                      src={course.thumbnail}
                      alt={course.title}
                      className="w-full h-32 object-cover rounded-lg mb-3"
                    />
                  ) : (
                    <div className="w-full h-32 bg-gradient-to-br from-purple-100 to-pink-100 rounded-lg mb-3 flex items-center justify-center">
                      <BookOpen className="w-12 h-12 text-blue-400" />
                    </div>
                  )}
                  <h3 className="font-semibold text-gray-900 mb-1 line-clamp-2">{course.title || 'ไม่มีชื่อ'}</h3>
                  <p className="text-sm text-gray-500 mb-2">ผู้สอน: {course.instructorName || 'N/A'}</p>
                  {course.courseId && (
                    <p className="text-xs font-mono text-blue-600 mb-2">รหัส: {course.courseId}</p>
                  )}
                  <div className="flex items-center justify-between text-xs text-gray-600">
                    <span className={`px-2 py-1 rounded-full ${
                      course.published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {course.published ? '✓ เผยแพร่' : '✕ ฉบับร่าง'}
                    </span>
                    <span>฿{course.price || 0}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-600 mb-4">
                แสดง {Math.min(6, courses.length)} จาก {courses.length} คอร์ส
              </p>
              <Link to="/admin/courses-management">
                <button className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                  <BookOpen className="w-5 h-5" />
                  จัดการคอร์สทั้งหมด
                </button>
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );

  const renderTotalRevenue = () => (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="bg-white rounded-2xl shadow-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
            <DollarSign className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-textDark">รายได้รวม</h2>
            <p className="text-sm text-textLight">สรุปรายได้ทั้งหมด</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
            <p className="text-sm text-gray-600 mb-2">รายได้รวม</p>
            <p className="text-3xl font-bold text-green-600">฿{safeStats.totalRevenue.toLocaleString()}</p>
            <div className="mt-2 flex items-center gap-1 text-xs text-green-600">
              <TrendingUp className="w-3 h-3" />
              <span>+{safeStats.growthRate.toFixed(1)}%</span>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-200">
            <p className="text-sm text-gray-600 mb-2">เดือนนี้</p>
            <p className="text-3xl font-bold text-blue-600">฿{safeStats.revenueThisMonth.toLocaleString()}</p>
          </div>
          
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-blue-200">
            <p className="text-sm text-gray-600 mb-2">เดือนที่แล้ว</p>
            <p className="text-3xl font-bold text-blue-600">฿{safeStats.revenueLastMonth.toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Revenue Chart */}
      <RevenueChart />

      {/* Tax Report Export */}
      <TaxReportExport />
    </div>
  );

  const renderFlaggedCourses = () => {
    const reasonLabels: Record<string, string> = {
      inappropriate: 'เนื้อหาไม่เหมาะสม',
      misleading: 'ข้อมูลเท็จหรือทำให้เข้าใจผิด',
      copyright: 'ละเมิดลิขสิทธิ์',
      spam: 'สแปมหรือโฆษณา',
      quality: 'คุณภาพต่ำ/ไม่ตรงกับคำอธิบาย',
      scam: 'หลอกลวง',
      other: 'อื่นๆ',
    };

    const statusColors: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-red-100 text-red-800',
      rejected: 'bg-gray-100 text-gray-800',
    };

    const statusLabels: Record<string, string> = {
      pending: 'รอตรวจสอบ',
      approved: 'ยืนยันการรายงาน',
      rejected: 'ปฏิเสธการรายงาน',
    };

    return (
      <div className="bg-white rounded-2xl shadow-xl">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-pink-600 rounded-xl flex items-center justify-center">
                <Flag className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-textDark">คอร์สที่ถูกรายงาน</h2>
                <p className="text-sm text-textLight">ตรวจสอบและจัดการคอร์สที่ถูกรายงาน</p>
              </div>
            </div>
            <button
              onClick={loadReportedCourses}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-semibold"
            >
              รีเฟรช
            </button>
          </div>
        </div>
        
        <div className="p-6">
          {loadingReports ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
              <p className="text-gray-500">กำลังโหลดข้อมูล...</p>
            </div>
          ) : reportedCourses.length === 0 ? (
            <div className="text-center py-12">
              <AlertTriangle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">ไม่มีคอร์สที่ถูกรายงานในขณะนี้</p>
            </div>
          ) : (
            <div className="space-y-4">
              {reportedCourses.map((report) => (
                <div key={report.id} className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-bold text-lg text-gray-900">{report.courseTitle}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusColors[report.status] || 'bg-gray-100 text-gray-800'}`}>
                          {statusLabels[report.status] || report.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-1">
                        <span className="font-semibold">รายงานโดย:</span> {report.reporterName} ({report.reporterEmail})
                      </p>
                      <p className="text-sm text-gray-600 mb-1">
                        <span className="font-semibold">เหตุผล:</span> {reasonLabels[report.reason] || report.reason}
                      </p>
                      {report.details && (
                        <p className="text-sm text-gray-600 mb-1">
                          <span className="font-semibold">รายละเอียด:</span> {report.details}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-2">
                        <Clock className="w-3 h-3 inline mr-1" />
                        {report.createdAt?.toDate?.()?.toLocaleString('th-TH') || 'N/A'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 mt-4 pt-4 border-t border-gray-100">
                    <Link to={`/courses/${report.courseId}`} target="_blank">
                      <button className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-semibold">
                        <Eye className="w-4 h-4" />
                        ดูคอร์ส
                      </button>
                    </Link>
                    {report.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleReportAction(report.id, 'approve')}
                          className="flex items-center gap-1 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-semibold"
                        >
                          <Ban className="w-4 h-4" />
                          ยืนยันการรายงาน
                        </button>
                        <button
                          onClick={() => handleReportAction(report.id, 'reject')}
                          className="flex items-center gap-1 px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-semibold"
                        >
                          <XCircle className="w-4 h-4" />
                          ปฏิเสธ
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => {
                        if (confirm('คุณต้องการลบรายงานนี้หรือไม่?')) {
                          handleReportAction(report.id, 'delete');
                        }
                      }}
                      className="flex items-center gap-1 px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm font-semibold ml-auto"
                    >
                      <Trash2 className="w-4 h-4" />
                      ลบรายงาน
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSubscriptionPayments = () => {
    const formatDate = (date: Date | null) => {
      if (!date) return 'ไม่ระบุ';
      return new Intl.DateTimeFormat('th-TH', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    };

    return (
      <div className="bg-white rounded-2xl shadow-xl">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-textDark">การชำระเงิน</h2>
              <p className="text-sm text-textLight">รายการการชำระเงินที่สำเร็จแล้ว</p>
            </div>
          </div>
        </div>
        <div className="p-6">
          {loadingPayments ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-500">กำลังโหลด...</p>
            </div>
          ) : (
            <div className="space-y-3">
              {subscriptionPayments.map((payment) => (
                <div key={payment.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div>
                    <p className="font-medium text-gray-900">{payment.instructorEmail || payment.email || 'ไม่ระบุอีเมล'}</p>
                    <p className="text-sm text-gray-500">{payment.plan?.toUpperCase() || 'PRO'} Package</p>
                    <p className="text-xs text-gray-400 mt-1">{formatDate(payment.createdAt)}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-green-600">฿{Number(payment.amount || 0).toLocaleString()}</p>
                    <p className="text-xs text-green-500">สำเร็จ</p>
                  </div>
                </div>
              ))}
              {subscriptionPayments.length === 0 && (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">ไม่มีรายการชำระเงินในขณะนี้</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPaymentApproval = () => {
    const formatDate = (date: Date | null) => {
      if (!date) return 'ไม่ระบุ';
      return new Intl.DateTimeFormat('th-TH', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    };

    return (
      <div className="bg-white rounded-2xl shadow-xl">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-textDark">
                  อนุมัติการชำระเงิน
                  {!loadingPendingPayments && pendingPayments.length > 0 && (
                    <span className="ml-2 px-3 py-1 bg-blue-100 text-blue-800 text-sm font-semibold rounded-full">
                      {pendingPayments.length} รายการ
                    </span>
                  )}
                </h2>
                <p className="text-sm text-textLight">ตรวจสอบและอนุมัติการชำระเงินที่รออนุมัติ</p>
              </div>
            </div>
            <button
              onClick={loadPendingPayments}
              disabled={loadingPendingPayments}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loadingPendingPayments ? 'animate-spin' : ''}`} />
              รีเฟรช
            </button>
          </div>
        </div>
        <div className="p-6">
          {loadingPendingPayments ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-500">กำลังโหลดข้อมูล...</p>
              <p className="text-xs text-gray-400 mt-2">กรุณารอสักครู่</p>
            </div>
          ) : pendingPayments.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg font-medium mb-2">ไม่มีการชำระเงินที่รออนุมัติ</p>
              <p className="text-sm text-gray-400">ระบบจะแสดงรายการที่มีสถานะ "pending" ที่นี่</p>
              <button
                onClick={loadPendingPayments}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-semibold flex items-center gap-2 mx-auto"
              >
                <RefreshCw className="w-4 h-4" />
                ลองโหลดอีกครั้ง
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {pendingPayments.map((payment) => (
                <div key={payment.id} className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <p className="font-semibold text-gray-900 text-lg">{payment.instructorEmail || payment.email || 'ไม่ระบุอีเมล'}</p>
                        <span className={`px-2 py-1 text-xs font-semibold rounded ${
                          payment.type === 'addon' 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {payment.type === 'addon' ? 'ซื้อพื้นที่เพิ่ม' : 'สมัครสมาชิก'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        {payment.type === 'addon' 
                          ? `${payment.addonName || 'Addon'} (+${payment.storageAmount || 0}GB)` 
                          : `${payment.plan?.toUpperCase() || 'PRO'} Package`
                        }
                      </p>
                      <p className="text-xs text-gray-500 mt-1">ส่งเมื่อ: {formatDate(payment.createdAt)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-blue-600">฿{Number(payment.amount || 0).toLocaleString()}</p>
                      <span className="inline-block mt-1 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded">
                        รออนุมัติ
                      </span>
                    </div>
                  </div>
                  
                  {payment.slipUrl && (
                    <div className="mb-3">
                      <p className="text-sm text-gray-600 mb-2">หลักฐานการโอนเงิน:</p>
                      <img 
                        src={payment.slipUrl} 
                        alt="Payment slip" 
                        className="max-w-xs rounded-lg border border-gray-300 cursor-pointer hover:opacity-90"
                        onClick={() => window.open(payment.slipUrl, '_blank')}
                      />
                    </div>
                  )}
                  
                  <div className="flex gap-3 mt-4">
                    <button
                      onClick={() => handlePaymentAction(payment.id, 'approve')}
                      className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center justify-center gap-2"
                    >
                      <CheckCircle className="w-5 h-5" />
                      อนุมัติ
                    </button>
                    <button
                      onClick={() => handlePaymentAction(payment.id, 'reject')}
                      className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium flex items-center justify-center gap-2"
                    >
                      <XCircle className="w-5 h-5" />
                      ปฏิเสธ
                    </button>
                  </div>
                </div>
              ))}
              {pendingPayments.length === 0 && (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">ไม่มีการชำระเงินที่รออนุมัติ</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSubscriptionManagement = () => (
    <div className="bg-white rounded-2xl shadow-xl h-full">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
              <Crown className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-textDark">จัดการ Subscription</h2>
              <p className="text-sm text-textLight">จัดการแพ็กเกจและสมาชิก</p>
            </div>
          </div>
          <a 
            href="https://coursedee.web.app/admin/subscriptions" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            เปิดในหน้าใหม่
          </a>
        </div>
      </div>
      <div className="p-0" style={{ height: 'calc(100vh - 200px)' }}>
        <iframe
          src="https://coursedee.web.app/admin/subscriptions"
          className="w-full h-full border-0"
          title="Subscription Management"
          sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
        />
      </div>
    </div>
  );

  const handleCreateDiscount = async () => {
    try {
      const { addDoc, collection, serverTimestamp } = await import('firebase/firestore');
      
      await addDoc(collection(db, 'discountCodes'), {
        code: discountForm.code.toUpperCase(),
        type: discountForm.type,
        value: Number(discountForm.value),
        maxUses: Number(discountForm.maxUses),
        currentUses: 0,
        expiresAt: discountForm.expiresAt ? new Date(discountForm.expiresAt) : null,
        description: discountForm.description,
        active: true,
        createdAt: serverTimestamp()
      });

      setShowDiscountModal(false);
      setDiscountForm({
        code: '',
        type: 'percentage',
        value: 0,
        maxUses: 0,
        expiresAt: '',
        description: ''
      });
      loadDiscountCodes();
      alert('สร้างโค้ดส่วนลดสำเร็จ!');
    } catch (error) {
      console.error('Error creating discount:', error);
      alert('เกิดข้อผิดพลาดในการสร้างโค้ดส่วนลด');
    }
  };

  const handleDeleteDiscount = async (id: string) => {
    if (!confirm('คุณต้องการลบโค้ดส่วนลดนี้หรือไม่?')) return;
    
    try {
      await deleteDoc(doc(db, 'discountCodes', id));
      loadDiscountCodes();
      alert('ลบโค้ดส่วนลดสำเร็จ!');
    } catch (error) {
      console.error('Error deleting discount:', error);
      alert('เกิดข้อผิดพลาดในการลบโค้ดส่วนลด');
    }
  };

  const handleToggleDiscount = async (id: string, currentStatus: boolean) => {
    try {
      await updateDoc(doc(db, 'discountCodes', id), {
        active: !currentStatus
      });
      loadDiscountCodes();
    } catch (error) {
      console.error('Error toggling discount:', error);
      alert('เกิดข้อผิดพลาด');
    }
  };

  const loadStorageStats = async () => {
    setLoadingStorage(true);
    try {
      const getStorageStatsFunc = httpsCallable(functions, 'getStorageStats');
      const result = await getStorageStatsFunc();
      
      if (result.data && (result.data as any).success) {
        setStorageStats((result.data as any).stats);
      }
    } catch (error) {
      console.error('Error loading storage stats:', error);
      alert('เกิดข้อผิดพลาดในการโหลดข้อมูล Storage');
    } finally {
      setLoadingStorage(false);
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return new Intl.DateTimeFormat('th-TH', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (error) {
      return 'N/A';
    }
  };

  const handleCleanOrphanedMedia = async (dryRun: boolean = true) => {
    setLoadingCleanup(true);
    try {
      const cleanOrphanedMediaFunc = httpsCallable(functions, 'cleanOrphanedMedia');
      const result = await cleanOrphanedMediaFunc({ dryRun });
      
      if (result.data && (result.data as any).success) {
        setCleanupResult((result.data as any).result);
        if (!dryRun) {
          alert(`ทำความสะอาดสำเร็จ! ลบไฟล์ orphaned ${(result.data as any).result.deleted} ไฟล์`);
          loadStorageStats(); // Reload storage stats
        }
      }
    } catch (error) {
      console.error('Error cleaning orphaned media:', error);
      alert('เกิดข้อผิดพลาดในการทำความสะอาด');
    } finally {
      setLoadingCleanup(false);
    }
  };

  const handleRecalculateStorage = async (userId?: string) => {
    setLoadingRecalculate(true);
    try {
      const recalculateFunc = httpsCallable(functions, 'recalculateStorageUsage');
      const result = await recalculateFunc({ userId });
      
      if (result.data && (result.data as any).success) {
        setRecalculateResult((result.data as any));
        alert(`กู้คืนข้อมูลสำเร็จ! อัปเดท ${(result.data as any).usersUpdated} ผู้ใช้`);
        loadStorageStats(); // Reload storage stats
      }
    } catch (error) {
      console.error('Error recalculating storage:', error);
      alert('เกิดข้อผิดพลาดในการกู้คืนข้อมูล');
    } finally {
      setLoadingRecalculate(false);
    }
  };

  const loadAllFiles = async (searchQuery: string = '') => {
    setLoadingAllFiles(true);
    try {
      const getAllFilesFunc = httpsCallable(functions, 'getAllFiles');
      const result = await getAllFilesFunc({ searchQuery });
      
      if (result.data && (result.data as any).success) {
        setAllFiles((result.data as any).files || []);
        setTotalFilesCount((result.data as any).totalFiles || 0);
        setTotalFilesSize((result.data as any).totalSize || 0);
      }
    } catch (error) {
      console.error('Error loading all files:', error);
      alert('เกิดข้อผิดพลาดในการโหลดไฟล์');
    } finally {
      setLoadingAllFiles(false);
    }
  };

  const handleDeleteFile = async (file: any) => {
    console.log('[handleDeleteFile] File object:', file);
    console.log('[handleDeleteFile] courseId:', file.courseId);
    console.log('[handleDeleteFile] mediaId:', file.id);
    
    if (!confirm(`คุณแน่ใจหรือไม่ที่จะลบไฟล์ "${file.fileName}"?\n\nการกระทำนี้ไม่สามารถย้อนกลับได้`)) {
      return;
    }

    try {
      const deleteFileFunc = httpsCallable(functions, 'deleteMediaFile');
      const result = await deleteFileFunc({ 
        courseId: file.courseId,
        mediaId: file.id 
      });
      
      if (result.data && (result.data as any).success) {
        alert('ลบไฟล์สำเร็จ!');
        loadStorageStats();
        if (showAllFiles) {
          loadAllFiles(fileSearchQuery);
        }
        if (showInactiveFiles) {
          loadInactiveFiles(inactiveThreshold);
        }
      } else {
        alert('เกิดข้อผิดพลาด: ' + ((result.data as any).error || 'Unknown error'));
      }
    } catch (error: any) {
      console.error('Error deleting file:', error);
      alert('เกิดข้อผิดพลาดในการลบไฟล์: ' + (error.message || 'Unknown error'));
    }
  };

  const handleBulkDeleteSearch = async () => {
    if (selectedSearchFiles.size === 0) {
      alert('กรุณาเลือกไฟล์ที่ต้องการลบ');
      return;
    }

    const selectedFiles = allFiles.filter(file => selectedSearchFiles.has(file.id));
    const totalSize = selectedFiles.reduce((sum, file) => sum + file.fileSize, 0);
    
    if (!confirm(
      `คุณแน่ใจหรือไม่ที่จะลบไฟล์ ${selectedSearchFiles.size} ไฟล์?\n\n` +
      `ขนาดรวม: ${formatBytes(totalSize)}\n\n` +
      `การกระทำนี้ไม่สามารถย้อนกลับได้`
    )) {
      return;
    }

    setDeletingBulkSearch(true);
    let successCount = 0;
    let errorCount = 0;
    const errors: string[] = [];

    try {
      const deleteFileFunc = httpsCallable(functions, 'deleteMediaFile');
      
      for (const file of selectedFiles) {
        try {
          const result = await deleteFileFunc({ 
            courseId: file.courseId,
            mediaId: file.id 
          });
          
          if (result.data && (result.data as any).success) {
            successCount++;
          } else {
            errorCount++;
            errors.push(`${file.fileName}: ${(result.data as any).error || 'Unknown error'}`);
          }
        } catch (error: any) {
          errorCount++;
          errors.push(`${file.fileName}: ${error.message || 'Unknown error'}`);
        }
      }

      let message = `ลบไฟล์เสร็จสิ้น!\n\n`;
      message += `สำเร็จ: ${successCount} ไฟล์\n`;
      if (errorCount > 0) {
        message += `ล้มเหลว: ${errorCount} ไฟล์\n\n`;
        if (errors.length > 0) {
          message += `รายละเอียดข้อผิดพลาด:\n${errors.slice(0, 5).join('\n')}`;
          if (errors.length > 5) {
            message += `\n... และอีก ${errors.length - 5} ข้อผิดพลาด`;
          }
        }
      }
      
      alert(message);
      
      setSelectedSearchFiles(new Set());
      loadStorageStats();
      if (showAllFiles) {
        loadAllFiles(fileSearchQuery);
      }
    } catch (error: any) {
      console.error('Error in bulk delete:', error);
      alert('เกิดข้อผิดพลาดในการลบไฟล์: ' + (error.message || 'Unknown error'));
    } finally {
      setDeletingBulkSearch(false);
    }
  };

  const toggleSelectAllSearchFiles = () => {
    if (selectedSearchFiles.size === allFiles.length) {
      setSelectedSearchFiles(new Set());
    } else {
      setSelectedSearchFiles(new Set(allFiles.map(file => file.id)));
    }
  };

  const toggleSelectSearchFile = (fileId: string) => {
    const newSelected = new Set(selectedSearchFiles);
    if (newSelected.has(fileId)) {
      newSelected.delete(fileId);
    } else {
      newSelected.add(fileId);
    }
    setSelectedSearchFiles(newSelected);
  };

  const loadActivityLogs = async () => {
    setLoadingActivityLogs(true);
    try {
      const logsQuery = query(
        collection(db, 'activityLogs'),
        orderBy('timestamp', 'desc'),
        limit(50)
      );
      const snapshot = await getDocs(logsQuery);
      const logs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setActivityLogs(logs);
    } catch (error) {
      console.error('Error loading activity logs:', error);
      setActivityLogs([]);
    } finally {
      setLoadingActivityLogs(false);
    }
  };

  const loadNotifications = async () => {
    setLoadingNotifications(true);
    try {
      const notifQuery = query(
        collection(db, 'notifications'),
        where('role', 'in', ['admin', 'all']),
        orderBy('createdAt', 'desc'),
        limit(20)
      );
      const snapshot = await getDocs(notifQuery);
      const notifs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setNotifications(notifs);
    } catch (error) {
      console.error('Error loading notifications:', error);
      setNotifications([]);
    } finally {
      setLoadingNotifications(false);
    }
  };

  const loadAnalytics = async () => {
    setLoadingAnalytics(true);
    try {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // นับผู้ใช้ใหม่ 7 วันล่าสุด
      const usersQuery = query(
        collection(db, 'users'),
        where('createdAt', '>=', sevenDaysAgo)
      );
      const usersSnapshot = await getDocs(usersQuery);
      const newUsers7Days = usersSnapshot.size;

      // นับคอร์สใหม่ 7 วันล่าสุด
      const coursesQuery = query(
        collection(db, 'courses'),
        where('createdAt', '>=', sevenDaysAgo)
      );
      const coursesSnapshot = await getDocs(coursesQuery);
      const newCourses7Days = coursesSnapshot.size;

      // คำนวณอัตราการเติบโต (เทียบกับ 7 วันก่อนหน้า)
      const fourteenDaysAgo = new Date();
      fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
      
      const prevUsersQuery = query(
        collection(db, 'users'),
        where('createdAt', '>=', fourteenDaysAgo),
        where('createdAt', '<', sevenDaysAgo)
      );
      const prevUsersSnapshot = await getDocs(prevUsersQuery);
      const prevUsers = prevUsersSnapshot.size;

      const growthRate = prevUsers > 0 
        ? ((newUsers7Days - prevUsers) / prevUsers * 100).toFixed(1)
        : newUsers7Days > 0 ? 100 : 0;

      setAnalyticsData({
        newUsers7Days,
        newCourses7Days,
        visitsToday: 0, // จะต้องมีระบบ tracking visits
        growthRate: Number(growthRate)
      });
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const loadUsageStats = async () => {
    setLoadingUsageStats(true);
    try {
      // นับผู้ใช้ที่ active (login ภายใน 30 วัน)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const activeUsersQuery = query(
        collection(db, 'users'),
        where('lastLoginAt', '>=', thirtyDaysAgo)
      );
      const activeUsersSnapshot = await getDocs(activeUsersQuery);
      const activeUsers = activeUsersSnapshot.size;

      // นับคอร์สที่เปิดใช้งาน
      const activeCoursesQuery = query(
        collection(db, 'courses'),
        where('published', '==', true)
      );
      const activeCoursesSnapshot = await getDocs(activeCoursesQuery);
      const activeCourses = activeCoursesSnapshot.size;

      setUsageStats({
        activeUsers,
        activeCourses
      });
    } catch (error) {
      console.error('Error loading usage stats:', error);
    } finally {
      setLoadingUsageStats(false);
    }
  };

  const loadSystemHealth = async () => {
    setLoadingSystemHealth(true);
    try {
      // ตรวจสอบ Firebase
      let firebaseStatus = { status: 'operational', message: 'ทำงานปกติ' };
      try {
        await getDocs(query(collection(db, 'users'), limit(1)));
      } catch (error) {
        firebaseStatus = { status: 'error', message: 'เกิดข้อผิดพลาด' };
      }

      // ตรวจสอบ Authentication
      let authStatus = { status: 'operational', message: 'ทำงานปกติ' };
      try {
        if (!user) {
          authStatus = { status: 'warning', message: 'ไม่ได้ล็อกอิน' };
        }
      } catch (error) {
        authStatus = { status: 'error', message: 'เกิดข้อผิดพลาด' };
      }

      // Cloudflare R2 และ Database ใช้ค่าเริ่มต้น (ต้องมี API endpoint เพื่อตรวจสอบจริง)
      setSystemHealth({
        firebase: firebaseStatus,
        cloudflareR2: { status: 'operational', message: 'ทำงานปกติ' },
        authentication: authStatus,
        database: firebaseStatus
      });
    } catch (error) {
      console.error('Error loading system health:', error);
    } finally {
      setLoadingSystemHealth(false);
    }
  };

  const loadInactiveFiles = async (months: number) => {
    setLoadingInactiveFiles(true);
    try {
      const getInactiveFilesFunc = httpsCallable(functions, 'getInactiveFiles');
      const result = await getInactiveFilesFunc({ months });
      
      if (result.data && (result.data as any).success) {
        setInactiveFiles((result.data as any).files || []);
      }
    } catch (error) {
      console.error('Error loading inactive files:', error);
      alert('เกิดข้อผิดพลาดในการโหลดไฟล์');
    } finally {
      setLoadingInactiveFiles(false);
    }
  };

  const toggleFileSelection = (fileId: string) => {
    const newSelected = new Set(selectedFiles);
    if (newSelected.has(fileId)) {
      newSelected.delete(fileId);
    } else {
      newSelected.add(fileId);
    }
    setSelectedFiles(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedFiles.size === inactiveFiles.length) {
      setSelectedFiles(new Set());
    } else {
      setSelectedFiles(new Set(inactiveFiles.map(f => f.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedFiles.size === 0) {
      alert('กรุณาเลือกไฟล์ที่ต้องการลบ');
      return;
    }

    if (!confirm(`คุณแน่ใจหรือไม่ที่จะลบไฟล์ ${selectedFiles.size} ไฟล์?\n\nการกระทำนี้ไม่สามารถย้อนกลับได้`)) {
      return;
    }

    setDeletingBulk(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      const deleteFileFunc = httpsCallable(functions, 'deleteMediaFile');

      for (const fileId of selectedFiles) {
        const file = inactiveFiles.find(f => f.id === fileId);
        if (!file) continue;

        try {
          const result = await deleteFileFunc({ 
            courseId: file.courseId,
            mediaId: file.id 
          });
          
          if (result.data && (result.data as any).success) {
            successCount++;
          } else {
            errorCount++;
          }
        } catch (error) {
          console.error('Error deleting file:', fileId, error);
          errorCount++;
        }
      }

      alert(`ลบไฟล์เสร็จสิ้น!\n\nสำเร็จ: ${successCount} ไฟล์\nล้มเหลว: ${errorCount} ไฟล์`);
      setSelectedFiles(new Set());
      loadStorageStats();
      loadInactiveFiles(inactiveThreshold);
    } catch (error) {
      console.error('Error in bulk delete:', error);
      alert('เกิดข้อผิดพลาดในการลบไฟล์');
    } finally {
      setDeletingBulk(false);
    }
  };

  const loadMarkedFiles = async () => {
    setLoadingMarkedFiles(true);
    try {
      const getMarkedFilesFunc = httpsCallable(functions, 'getMarkedFiles');
      const result = await getMarkedFilesFunc();
      
      if (result.data && (result.data as any).success) {
        setMarkedFiles((result.data as any).files || []);
      }
    } catch (error) {
      console.error('Error loading marked files:', error);
      alert('เกิดข้อผิดพลาดในการโหลดไฟล์รอลบ');
    } finally {
      setLoadingMarkedFiles(false);
    }
  };

  const handleRestoreFile = async (file: any) => {
    if (!confirm(`คุณต้องการกู้คืนไฟล์ "${file.fileName}" หรือไม่?`)) {
      return;
    }

    setRestoringFile(file.id);
    try {
      const restoreFileFunc = httpsCallable(functions, 'restoreMarkedFile');
      const result = await restoreFileFunc({ 
        courseId: file.courseId,
        mediaId: file.id 
      });
      
      if (result.data && (result.data as any).success) {
        alert('กู้คืนไฟล์สำเร็จ!');
        loadMarkedFiles();
        loadStorageStats();
      } else {
        alert('เกิดข้อผิดพลาดในการกู้คืนไฟล์');
      }
    } catch (error) {
      console.error('Error restoring file:', error);
      alert('เกิดข้อผิดพลาดในการกู้คืนไฟล์');
    } finally {
      setRestoringFile(null);
    }
  };

  const handleDeleteMarkedFile = async (file: any) => {
    if (!confirm(`คุณแน่ใจหรือไม่ที่จะลบไฟล์ "${file.fileName}" ทันที?\n\nการกระทำนี้ไม่สามารถย้อนกลับได้`)) {
      return;
    }

    setDeletingMarkedFile(file.id);
    try {
      const deleteFileFunc = httpsCallable(functions, 'deleteMarkedFile');
      const result = await deleteFileFunc({ 
        courseId: file.courseId,
        mediaId: file.id 
      });
      
      if (result.data && (result.data as any).success) {
        alert('ลบไฟล์สำเร็จ!');
        loadMarkedFiles();
        loadStorageStats();
      } else {
        alert('เกิดข้อผิดพลาดในการลบไฟล์');
      }
    } catch (error) {
      console.error('Error deleting marked file:', error);
      alert('เกิดข้อผิดพลาดในการลบไฟล์');
    } finally {
      setDeletingMarkedFile(null);
    }
  };

  const renderDiscountManagement = () => (
    <div className="bg-white rounded-2xl shadow-xl">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-rose-600 rounded-xl flex items-center justify-center">
              <Tag className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-textDark">จัดการส่วนลด</h2>
              <p className="text-sm text-textLight">สร้างและจัดการโค้ดส่วนลด</p>
            </div>
          </div>
          <button
            onClick={() => setShowDiscountModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-500 to-rose-600 text-white rounded-lg hover:shadow-lg transition-all"
          >
            <Plus className="w-5 h-5" />
            สร้างโค้ดใหม่
          </button>
        </div>
      </div>
      <div className="p-6">
        {loadingDiscounts ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mx-auto"></div>
            <p className="text-gray-500 mt-4">กำลังโหลด...</p>
          </div>
        ) : discountCodes.length === 0 ? (
          <div className="text-center py-8">
            <Tag className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">ยังไม่มีโค้ดส่วนลดในระบบ</p>
            <button
              onClick={() => setShowDiscountModal(true)}
              className="mt-4 px-6 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors"
            >
              สร้างโค้ดแรกของคุณ
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {discountCodes.map((discount) => (
              <div key={discount.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xl font-bold text-pink-600">{discount.code}</span>
                      {discount.active ? (
                        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">ใช้งานได้</span>
                      ) : (
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">ปิดใช้งาน</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{discount.description}</p>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                      <span>
                        ส่วนลด: <strong className="text-pink-600">
                          {discount.type === 'percentage' ? `${discount.value}%` : `${discount.value} บาท`}
                        </strong>
                      </span>
                      <span>
                        ใช้ไป: <strong>{discount.currentUses || 0}</strong> / {discount.maxUses || '∞'}
                      </span>
                      {discount.expiresAt && (
                        <span>
                          หมดอายุ: <strong>{new Date(discount.expiresAt.seconds * 1000).toLocaleDateString('th-TH')}</strong>
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleDiscount(discount.id, discount.active)}
                      className={`p-2 rounded-lg transition-colors ${
                        discount.active
                          ? 'bg-green-100 text-green-600 hover:bg-green-200'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      title={discount.active ? 'ปิดใช้งาน' : 'เปิดใช้งาน'}
                    >
                      {discount.active ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                    </button>
                    <button
                      onClick={() => handleDeleteDiscount(discount.id)}
                      className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                      title="ลบ"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal สร้างโค้ดส่วนลด */}
      {showDiscountModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-textDark">สร้างโค้ดส่วนลดใหม่</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  รหัสโค้ด <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={discountForm.code}
                  onChange={(e) => setDiscountForm({ ...discountForm, code: e.target.value })}
                  placeholder="เช่น SALE50"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent uppercase"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ประเภทส่วนลด <span className="text-red-500">*</span>
                </label>
                <select
                  value={discountForm.type}
                  onChange={(e) => setDiscountForm({ ...discountForm, type: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                >
                  <option value="percentage">เปอร์เซ็นต์ (%)</option>
                  <option value="fixed">จำนวนเงิน (บาท)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  มูลค่าส่วนลด <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={discountForm.value}
                  onChange={(e) => setDiscountForm({ ...discountForm, value: Number(e.target.value) })}
                  placeholder={discountForm.type === 'percentage' ? '0-100' : '0'}
                  min="0"
                  max={discountForm.type === 'percentage' ? 100 : undefined}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  จำนวนครั้งที่ใช้ได้สูงสุด
                </label>
                <input
                  type="number"
                  value={discountForm.maxUses}
                  onChange={(e) => setDiscountForm({ ...discountForm, maxUses: Number(e.target.value) })}
                  placeholder="0 = ไม่จำกัด"
                  min="0"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  วันหมดอายุ
                </label>
                <input
                  type="date"
                  value={discountForm.expiresAt}
                  onChange={(e) => setDiscountForm({ ...discountForm, expiresAt: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  คำอธิบาย
                </label>
                <textarea
                  value={discountForm.description}
                  onChange={(e) => setDiscountForm({ ...discountForm, description: e.target.value })}
                  placeholder="อธิบายเงื่อนไขหรือรายละเอียดโค้ดส่วนลด"
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => {
                  setShowDiscountModal(false);
                  setDiscountForm({
                    code: '',
                    type: 'percentage',
                    value: 0,
                    maxUses: 0,
                    expiresAt: '',
                    description: ''
                  });
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                ยกเลิก
              </button>
              <button
                onClick={handleCreateDiscount}
                disabled={!discountForm.code || !discountForm.value}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-pink-500 to-rose-600 text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                สร้างโค้ด
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderStorageManagement = () => (
    <div className="bg-white rounded-2xl shadow-xl">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
              <HardDrive className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-textDark">จัดการพื้นที่เก็บข้อมูล</h2>
              <p className="text-sm text-textLight">ตรวจสอบการใช้พื้นที่และจัดการไฟล์</p>
            </div>
          </div>
          <button
            onClick={loadStorageStats}
            disabled={loadingStorage}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-semibold disabled:opacity-50"
          >
            {loadingStorage ? 'กำลังโหลด...' : 'รีเฟรช'}
          </button>
        </div>
      </div>
      <div className="p-6">
        {loadingStorage ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-500">กำลังโหลดข้อมูล Storage...</p>
          </div>
        ) : !storageStats ? (
          <div className="text-center py-12">
            <HardDrive className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">คลิกปุ่ม "รีเฟรช" เพื่อโหลดข้อมูล Storage</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Storage Overview */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                <div className="flex items-center gap-2 mb-2">
                  <HardDrive className="w-5 h-5 text-blue-600" />
                  <p className="text-sm text-gray-600 font-medium">พื้นที่ใช้ไปทั้งหมด</p>
                </div>
                <p className="text-2xl font-bold text-blue-600">{formatBytes(storageStats.totalStorageBytes)}</p>
                <p className="text-xs text-gray-500 mt-1">{storageStats.totalFiles.toLocaleString()} ไฟล์</p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-blue-200">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  <p className="text-sm text-gray-600 font-medium">วิดีโอ</p>
                </div>
                <p className="text-2xl font-bold text-blue-600">{formatBytes(storageStats.videoStorageBytes)}</p>
                <p className="text-xs text-gray-500 mt-1">{storageStats.videoCount.toLocaleString()} ไฟล์</p>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="w-5 h-5 text-green-600" />
                  <p className="text-sm text-gray-600 font-medium">รูปภาพ</p>
                </div>
                <p className="text-2xl font-bold text-green-600">{formatBytes(storageStats.imageStorageBytes)}</p>
                <p className="text-xs text-gray-500 mt-1">{storageStats.imageCount.toLocaleString()} ไฟล์</p>
              </div>
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border border-blue-200">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  <p className="text-sm text-gray-600 font-medium">เอกสาร</p>
                </div>
                <p className="text-2xl font-bold text-blue-600">{formatBytes(storageStats.fileStorageBytes)}</p>
                <p className="text-xs text-gray-500 mt-1">{storageStats.fileCount.toLocaleString()} ไฟล์</p>
              </div>
            </div>

            {/* Storage Actions Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowStorageActionsMenu(!showStorageActionsMenu)}
                className="w-full px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:shadow-lg transition-all font-semibold flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <Settings className="w-5 h-5" />
                  <span>เครื่องมือจัดการไฟล์</span>
                </div>
                {showStorageActionsMenu ? (
                  <ChevronUp className="w-5 h-5" />
                ) : (
                  <ChevronDown className="w-5 h-5" />
                )}
              </button>

              {showStorageActionsMenu && (
                <div className="mt-3 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                  <div className="divide-y divide-gray-100">
                    <button
                      onClick={() => {
                        setShowTopUsers(!showTopUsers);
                        setShowStorageActionsMenu(false);
                      }}
                      className="w-full px-4 py-3 hover:bg-blue-50 transition-colors flex items-center gap-3 text-left"
                    >
                      <Users className="w-5 h-5 text-blue-600" />
                      <span className="font-medium text-gray-900">{showTopUsers ? 'ซ่อน' : 'แสดง'} ผู้ใช้ที่ใช้พื้นที่มากที่สุด</span>
                    </button>

                    <button
                      onClick={() => {
                        setShowFileList(!showFileList);
                        setShowStorageActionsMenu(false);
                      }}
                      className="w-full px-4 py-3 hover:bg-blue-50 transition-colors flex items-center gap-3 text-left"
                    >
                      <FileText className="w-5 h-5 text-blue-600" />
                      <span className="font-medium text-gray-900">{showFileList ? 'ซ่อน' : 'แสดง'} ไฟล์ล่าสุด</span>
                    </button>

                    <button
                      onClick={() => {
                        setShowAllFiles(!showAllFiles);
                        if (!showAllFiles) {
                          setAllFiles([]);
                          setTotalFilesCount(0);
                          setTotalFilesSize(0);
                          setFileSearchQuery('');
                        }
                        setShowStorageActionsMenu(false);
                      }}
                      disabled={loadingAllFiles}
                      className="w-full px-4 py-3 hover:bg-indigo-50 transition-colors flex items-center gap-3 text-left disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Search className="w-5 h-5 text-indigo-600" />
                      <span className="font-medium text-gray-900">{showAllFiles ? 'ซ่อน' : 'เปิด'} ค้นหาไฟล์</span>
                    </button>

                    <button
                      onClick={() => {
                        setShowMarkedFiles(!showMarkedFiles);
                        if (!showMarkedFiles) {
                          loadMarkedFiles();
                        }
                        setShowStorageActionsMenu(false);
                      }}
                      disabled={loadingMarkedFiles}
                      className="w-full px-4 py-3 hover:bg-yellow-50 transition-colors flex items-center gap-3 text-left disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Clock className="w-5 h-5 text-yellow-600" />
                      <span className="font-medium text-gray-900">{showMarkedFiles ? 'ซ่อน' : 'แสดง'} ไฟล์รอลบ</span>
                    </button>

                    <button
                      onClick={() => {
                        setShowInactiveFiles(!showInactiveFiles);
                        if (!showInactiveFiles) {
                          setInactiveFiles([]);
                          setSelectedFiles(new Set());
                        }
                        setShowStorageActionsMenu(false);
                      }}
                      disabled={loadingInactiveFiles}
                      className="w-full px-4 py-3 hover:bg-blue-50 transition-colors flex items-center gap-3 text-left disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <AlertTriangle className="w-5 h-5 text-blue-600" />
                      <span className="font-medium text-gray-900">{showInactiveFiles ? 'ซ่อน' : 'ตรวจสอบ'} ไฟล์ไม่ได้ใช้งาน</span>
                    </button>

                    <button
                      onClick={() => {
                        setShowCleanupModal(true);
                        setShowStorageActionsMenu(false);
                      }}
                      disabled={loadingCleanup}
                      className="w-full px-4 py-3 hover:bg-red-50 transition-colors flex items-center gap-3 text-left disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Trash2 className="w-5 h-5 text-red-600" />
                      <span className="font-medium text-gray-900">{loadingCleanup ? 'กำลังตรวจสอบ...' : 'หาและลบไฟล์ Orphaned'}</span>
                    </button>

                    <button
                      onClick={() => {
                        setShowRecalculateModal(true);
                        setShowStorageActionsMenu(false);
                      }}
                      disabled={loadingRecalculate}
                      className="w-full px-4 py-3 hover:bg-green-50 transition-colors flex items-center gap-3 text-left disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <RefreshCw className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-gray-900">{loadingRecalculate ? 'กำลังคำนวณ...' : 'กู้คืนข้อมูล Storage'}</span>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Cleanup Result */}
            {cleanupResult && (
              <div className="border border-blue-200 bg-blue-50 rounded-xl p-4">
                <h3 className="text-lg font-bold text-blue-900 mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-blue-600" />
                  ผลการตรวจสอบไฟล์ Orphaned
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="bg-white rounded-lg p-3">
                    <p className="text-xs text-gray-600 mb-1">ตรวจสอบทั้งหมด</p>
                    <p className="text-xl font-bold text-gray-900">{cleanupResult.totalChecked}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3">
                    <p className="text-xs text-gray-600 mb-1">พบ Orphaned</p>
                    <p className="text-xl font-bold text-blue-600">{cleanupResult.orphanedFound}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3">
                    <p className="text-xs text-gray-600 mb-1">ลบแล้ว</p>
                    <p className="text-xl font-bold text-green-600">{cleanupResult.deleted}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3">
                    <p className="text-xs text-gray-600 mb-1">Error</p>
                    <p className="text-xl font-bold text-red-600">{cleanupResult.errors}</p>
                  </div>
                </div>
                {cleanupResult.orphanedFiles.length > 0 && (
                  <div className="bg-white rounded-lg p-3 max-h-60 overflow-y-auto">
                    <p className="text-sm font-semibold text-gray-900 mb-2">รายการไฟล์ Orphaned:</p>
                    <div className="space-y-2">
                      {cleanupResult.orphanedFiles.slice(0, 10).map((file: any, index: number) => (
                        <div key={index} className="text-xs border-b border-gray-100 pb-2">
                          <p className="font-medium text-gray-900">{file.fileName}</p>
                          <p className="text-gray-500">เหตุผล: {file.reason}</p>
                        </div>
                      ))}
                      {cleanupResult.orphanedFiles.length > 10 && (
                        <p className="text-xs text-gray-500 italic">และอีก {cleanupResult.orphanedFiles.length - 10} ไฟล์...</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Top Storage Users */}
            {showTopUsers && storageStats.topUsers.length > 0 && (
              <div className="border border-gray-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-600" />
                    ผู้ใช้ที่ใช้พื้นที่มากที่สุด (Top 10)
                  </h3>
                  <button
                    onClick={() => setShowTopUsers(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    title="ซ่อน"
                  >
                    <XCircle className="w-5 h-5 text-gray-500 hover:text-gray-700" />
                  </button>
                </div>
                <div className="space-y-2">
                  {storageStats.topUsers.map((user: any, index: number) => (
                    <div key={user.userId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-bold text-gray-400 w-6">#{index + 1}</span>
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-blue-600">
                            {user.userName?.[0]?.toUpperCase() || '?'}
                          </span>
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{user.userName}</p>
                          <p className="text-xs text-gray-500">{user.userEmail}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-blue-600">{formatBytes(user.storageBytes)}</p>
                        <p className="text-xs text-gray-500">{user.fileCount} ไฟล์</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Files */}
            {showFileList && storageStats.recentFiles.length > 0 && (
              <div className="border border-gray-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    ไฟล์ที่อัปโหลดล่าสุด (20 ไฟล์)
                  </h3>
                  <button
                    onClick={() => setShowFileList(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    title="ซ่อน"
                  >
                    <XCircle className="w-5 h-5 text-gray-500 hover:text-gray-700" />
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">ชื่อไฟล์</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">ประเภท</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">ขนาด</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">อัปโหลดโดย</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">วันที่</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">การกระทำ</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {storageStats.recentFiles.map((file: any) => (
                        <tr key={file.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">
                            <div className="max-w-xs truncate" title={file.fileName}>
                              {file.fileName}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              file.type === 'video' ? 'bg-blue-100 text-blue-700' :
                              file.type === 'image' ? 'bg-green-100 text-green-700' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {file.type}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">{formatBytes(file.fileSize)}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            <div className="max-w-xs truncate" title={file.uploaderEmail}>
                              {file.uploaderName}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">{formatDate(file.createdAt)}</td>
                          <td className="px-4 py-3 text-sm">
                            <div className="flex items-center gap-2">
                              {file.url && (
                                <a
                                  href={file.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-800 transition-colors"
                                  title="ดูไฟล์"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </a>
                              )}
                              <button
                                onClick={() => handleDeleteFile(file)}
                                className="text-red-600 hover:text-red-800 transition-colors"
                                title="ลบไฟล์"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* All Files with Search */}
            {showAllFiles && (
              <div className="border border-gray-200 rounded-xl p-4">
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        <Search className="w-5 h-5 text-indigo-600" />
                        ค้นหาไฟล์
                      </h3>
                      {totalFilesCount > 0 && (
                        <div className="text-sm text-gray-600">
                          <span className="font-semibold">{totalFilesCount.toLocaleString()}</span> ไฟล์ 
                          <span className="mx-2">•</span>
                          <span className="font-semibold">{formatBytes(totalFilesSize)}</span>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        setShowAllFiles(false);
                        setAllFiles([]);
                        setTotalFilesCount(0);
                        setTotalFilesSize(0);
                        setFileSearchQuery('');
                        setSelectedSearchFiles(new Set());
                      }}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      title="ซ่อน"
                    >
                      <XCircle className="w-5 h-5 text-gray-500 hover:text-gray-700" />
                    </button>
                  </div>
                  
                  {/* Search Box */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="ค้นหาชื่อไฟล์... (เช่น Tartay, ประกัน, .xlsx)"
                      value={fileSearchQuery}
                      onChange={(e) => setFileSearchQuery(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          loadAllFiles(fileSearchQuery);
                        }
                      }}
                      className="w-full pl-10 pr-24 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                    <button
                      onClick={() => loadAllFiles(fileSearchQuery)}
                      disabled={loadingAllFiles}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 px-4 py-1.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors text-sm font-medium disabled:opacity-50"
                    >
                      {loadingAllFiles ? 'กำลังค้นหา...' : 'ค้นหา'}
                    </button>
                  </div>
                  
                  {fileSearchQuery && (
                    <p className="text-sm text-gray-500 mt-2">
                      ผลการค้นหา: "<span className="font-semibold text-indigo-600">{fileSearchQuery}</span>"
                    </p>
                  )}
                </div>

                {/* Bulk Actions */}
                {allFiles.length > 0 && (
                  <div className="mb-4 flex items-center justify-between bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 border border-indigo-200">
                    <div className="flex items-center gap-4">
                      <button
                        onClick={toggleSelectAllSearchFiles}
                        className="px-4 py-2 bg-white border border-indigo-300 text-indigo-700 rounded-lg hover:bg-indigo-50 transition-colors text-sm font-medium flex items-center gap-2"
                      >
                        {selectedSearchFiles.size === allFiles.length ? (
                          <>
                            <XCircle className="w-4 h-4" />
                            ยกเลิกการเลือกทั้งหมด
                          </>
                        ) : (
                          <>
                            <CheckSquare className="w-4 h-4" />
                            เลือกทั้งหมด
                          </>
                        )}
                      </button>
                      {selectedSearchFiles.size > 0 && (
                        <div className="text-sm text-gray-700">
                          เลือกแล้ว <span className="font-bold text-indigo-600">{selectedSearchFiles.size}</span> ไฟล์
                          <span className="mx-2">•</span>
                          ขนาดรวม <span className="font-bold text-indigo-600">{formatBytes(allFiles.filter(f => selectedSearchFiles.has(f.id)).reduce((sum, f) => sum + f.fileSize, 0))}</span>
                        </div>
                      )}
                    </div>
                    {selectedSearchFiles.size > 0 && (
                      <button
                        onClick={handleBulkDeleteSearch}
                        disabled={deletingBulkSearch}
                        className="px-6 py-2 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 transition-all text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg"
                      >
                        <Trash2 className="w-4 h-4" />
                        {deletingBulkSearch ? 'กำลังลบ...' : `ลบที่เลือก (${selectedSearchFiles.size})`}
                      </button>
                    )}
                  </div>
                )}

                {loadingAllFiles ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                    <p className="text-gray-500">กำลังโหลดไฟล์...</p>
                  </div>
                ) : allFiles.length === 0 ? (
                  <div className="text-center py-12">
                    <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">
                      {fileSearchQuery ? 'ไม่พบไฟล์ที่ตรงกับคำค้นหา' : 'พิมพ์ชื่อไฟล์และกดปุ่ม "ค้นหา" เพื่อเริ่มค้นหา'}
                    </p>
                    {!fileSearchQuery && (
                      <p className="text-xs text-gray-400 mt-2">
                        💡 ตัวอย่าง: Tartay, ประกัน, .xlsx, .mp4
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase w-12">
                            <input
                              type="checkbox"
                              checked={selectedSearchFiles.size === allFiles.length && allFiles.length > 0}
                              onChange={toggleSelectAllSearchFiles}
                              className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
                            />
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">ชื่อไฟล์</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">คอร์ส</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">บทเรียน</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">ประเภท</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">ขนาด</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">เข้าถึงล่าสุด</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">จำนวนเข้าถึง</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">ดาวน์โหลดล่าสุด</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">จำนวนดาวน์โหลด</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">อัปโหลดโดย</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">วันที่อัปโหลด</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">การกระทำ</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {allFiles.map((file: any) => (
                          <tr key={file.id} className={`hover:bg-gray-50 transition-colors ${selectedSearchFiles.has(file.id) ? 'bg-indigo-50' : ''}`}>
                            <td className="px-4 py-3 text-center">
                              <input
                                type="checkbox"
                                checked={selectedSearchFiles.has(file.id)}
                                onChange={() => toggleSelectSearchFile(file.id)}
                                className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
                              />
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <div className="max-w-xs truncate font-medium text-gray-900" title={file.fileName}>
                                {file.fileName}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <div className="max-w-xs truncate text-gray-600" title={file.courseName}>
                                {file.courseName}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <div className="max-w-xs truncate text-gray-600" title={file.lessonTitle || 'N/A'}>
                                {file.lessonTitle || 'N/A'}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                file.type === 'video' ? 'bg-blue-100 text-blue-700' :
                                file.type === 'image' ? 'bg-green-100 text-green-700' :
                                'bg-blue-100 text-blue-700'
                              }`}>
                                {file.type === 'video' ? 'วิดีโอ' : file.type === 'image' ? 'รูปภาพ' : 'เอกสาร'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">{formatBytes(file.fileSize)}</td>
                            <td className="px-4 py-3 text-sm text-gray-500">
                              {file.lastAccessAt ? formatDate(file.lastAccessAt) : '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-center">
                              <span className={`font-semibold ${file.accessCount > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                                {file.accessCount || 0}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500">
                              {file.lastDownloadAt ? formatDate(file.lastDownloadAt) : '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-center">
                              <span className={`font-semibold ${file.downloadCount > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                                {file.downloadCount || 0}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <div className="max-w-xs truncate" title={file.uploaderEmail}>
                                {file.uploaderName}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500">{formatDate(file.createdAt)}</td>
                            <td className="px-4 py-3 text-sm">
                              <div className="flex items-center gap-2">
                                {file.url && (
                                  <a
                                    href={file.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 transition-colors"
                                    title="ดูไฟล์"
                                  >
                                    <ExternalLink className="w-4 h-4" />
                                  </a>
                                )}
                                {file.originalUrl && file.type === 'video' && (
                                  <a
                                    href={file.originalUrl}
                                    download
                                    className="text-green-600 hover:text-green-800 transition-colors"
                                    title="ดาวน์โหลดต้นฉบับ"
                                  >
                                    <Save className="w-4 h-4" />
                                  </a>
                                )}
                                <button
                                  onClick={() => handleDeleteFile(file)}
                                  className="text-red-600 hover:text-red-800 transition-colors"
                                  title="ลบไฟล์"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
            {showInactiveFiles && (
              <div className="border border-blue-200 rounded-xl p-4 bg-blue-50">
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-gray-700">ไม่ได้ใช้งานมากกว่า:</label>
                        <select
                          value={inactiveThreshold}
                          onChange={(e) => setInactiveThreshold(Number(e.target.value))}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value={1}>1 เดือน</option>
                          <option value={3}>3 เดือน</option>
                          <option value={6}>6 เดือน</option>
                          <option value={12}>12 เดือน</option>
                        </select>
                      </div>
                      <button
                        onClick={() => loadInactiveFiles(inactiveThreshold)}
                        disabled={loadingInactiveFiles}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50"
                      >
                        <Search className="w-4 h-4 inline mr-2" />
                        ค้นหา
                      </button>
                    </div>
                    <button
                      onClick={() => {
                        setShowInactiveFiles(false);
                        setInactiveFiles([]);
                        setSelectedFiles(new Set());
                      }}
                      className="p-2 hover:bg-blue-100 rounded-lg transition-colors"
                      title="ซ่อน"
                    >
                      <XCircle className="w-5 h-5 text-blue-600 hover:text-blue-800" />
                    </button>
                  </div>

                  {selectedFiles.size > 0 && (
                    <button
                      onClick={handleBulkDelete}
                      disabled={deletingBulk}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold disabled:opacity-50 mb-4"
                    >
                      <Trash2 className="w-4 h-4 inline mr-2" />
                      {deletingBulk ? 'กำลังลบ...' : `ลบที่เลือก (${selectedFiles.size})`}
                    </button>
                  )}

                  {inactiveFiles.length > 0 && (
                    <div className="bg-white rounded-lg p-3 mb-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">
                          พบ <span className="font-bold text-blue-600">{inactiveFiles.length}</span> ไฟล์
                        </span>
                        <span className="text-gray-600">
                          ขนาดรวม <span className="font-bold text-blue-600">{formatBytes(inactiveFiles.reduce((sum, file) => sum + file.fileSize, 0))}</span>
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {loadingInactiveFiles ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-500">กำลังค้นหาไฟล์...</p>
                  </div>
                ) : inactiveFiles.length === 0 ? (
                  <div className="text-center py-12">
                    <AlertTriangle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">ไม่พบไฟล์ที่ไม่ได้ใช้งาน</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full bg-white rounded-lg">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left">
                            <button onClick={toggleSelectAll} className="hover:text-blue-600">
                              {selectedFiles.size === inactiveFiles.length ? (
                                <CheckSquare className="w-5 h-5" />
                              ) : (
                                <Square className="w-5 h-5" />
                              )}
                            </button>
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">ชื่อไฟล์</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">คอร์ส</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">ประเภท</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">ขนาด</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">ไม่ได้ใช้งาน</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">การกระทำ</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {inactiveFiles.map((file: any) => (
                          <tr key={file.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <button 
                                onClick={() => toggleFileSelection(file.id)}
                                className="hover:text-blue-600"
                              >
                                {selectedFiles.has(file.id) ? (
                                  <CheckSquare className="w-5 h-5 text-blue-600" />
                                ) : (
                                  <Square className="w-5 h-5" />
                                )}
                              </button>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <div className="max-w-xs truncate font-medium text-gray-900" title={file.fileName}>
                                {file.fileName}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <div className="max-w-xs truncate text-gray-600" title={file.courseName}>
                                {file.courseName}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                file.type === 'video' ? 'bg-blue-100 text-blue-700' :
                                file.type === 'image' ? 'bg-green-100 text-green-700' :
                                'bg-blue-100 text-blue-700'
                              }`}>
                                {file.type === 'video' ? 'วิดีโอ' : file.type === 'image' ? 'รูปภาพ' : 'เอกสาร'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">{formatBytes(file.fileSize)}</td>
                            <td className="px-4 py-3 text-sm">
                              <span className="text-blue-600 font-semibold">
                                {file.inactiveMonths} เดือน ({file.inactiveDays} วัน)
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <div className="flex items-center gap-2">
                                {file.url && (
                                  <a
                                    href={file.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 transition-colors"
                                    title="ดูไฟล์"
                                  >
                                    <ExternalLink className="w-4 h-4" />
                                  </a>
                                )}
                                <button
                                  onClick={() => handleDeleteFile(file)}
                                  className="text-red-600 hover:text-red-800 transition-colors"
                                  title="ลบไฟล์"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Marked Files Section */}
            {showMarkedFiles && (
              <div className="border border-yellow-200 rounded-xl p-4 bg-yellow-50">
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-yellow-900 flex items-center gap-2">
                      <Clock className="w-5 h-5 text-yellow-600" />
                      ไฟล์รอลบ (จากบทเรียนที่ถูกลบ)
                    </h3>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={loadMarkedFiles}
                        disabled={loadingMarkedFiles}
                        className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors font-semibold disabled:opacity-50"
                      >
                        <RefreshCw className="w-4 h-4 inline mr-2" />
                        รีเฟรช
                      </button>
                      <button
                        onClick={() => setShowMarkedFiles(false)}
                        className="p-2 hover:bg-yellow-100 rounded-lg transition-colors"
                        title="ซ่อน"
                      >
                        <XCircle className="w-5 h-5 text-yellow-600 hover:text-yellow-800" />
                      </button>
                    </div>
                  </div>

                  {markedFiles.length > 0 && (
                    <div className="bg-white rounded-lg p-3 mb-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">
                          พบ <span className="font-bold text-yellow-600">{markedFiles.length}</span> ไฟล์รอลบ
                        </span>
                        <span className="text-gray-600">
                          ขนาดรวม <span className="font-bold text-yellow-600">{formatBytes(markedFiles.reduce((sum, file) => sum + file.fileSize, 0))}</span>
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {loadingMarkedFiles ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-600 mx-auto mb-4"></div>
                    <p className="text-gray-500">กำลังโหลดไฟล์รอลบ...</p>
                  </div>
                ) : markedFiles.length === 0 ? (
                  <div className="text-center py-12">
                    <Clock className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">ไม่มีไฟล์รอลบ</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full bg-white rounded-lg">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">ชื่อไฟล์</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">บทเรียนที่ถูกลบ</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">คอร์ส</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">ประเภท</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">ขนาด</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">ลบอัตโนมัติใน</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">การกระทำ</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {markedFiles.map((file: any) => (
                          <tr key={file.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm">
                              <div className="max-w-xs truncate font-medium text-gray-900" title={file.fileName}>
                                {file.fileName}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <div className="max-w-xs truncate text-gray-600" title={file.deletedLessonTitle}>
                                {file.deletedLessonTitle}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <div className="max-w-xs truncate text-gray-600" title={file.courseName}>
                                {file.courseName}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                file.type === 'video' ? 'bg-blue-100 text-blue-700' :
                                file.type === 'image' ? 'bg-green-100 text-green-700' :
                                'bg-blue-100 text-blue-700'
                              }`}>
                                {file.type === 'video' ? 'วิดีโอ' : file.type === 'image' ? 'รูปภาพ' : 'เอกสาร'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">{formatBytes(file.fileSize)}</td>
                            <td className="px-4 py-3 text-sm">
                              <span className={`font-semibold ${
                                file.daysRemaining <= 7 ? 'text-red-600' :
                                file.daysRemaining <= 14 ? 'text-blue-600' :
                                'text-yellow-600'
                              }`}>
                                {file.daysRemaining} วัน
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleRestoreFile(file)}
                                  disabled={restoringFile === file.id}
                                  className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 transition-colors disabled:opacity-50 text-xs font-medium"
                                  title="กู้คืนไฟล์"
                                >
                                  {restoringFile === file.id ? (
                                    <RefreshCw className="w-3 h-3 animate-spin" />
                                  ) : (
                                    'กู้คืน'
                                  )}
                                </button>
                                <button
                                  onClick={() => handleDeleteMarkedFile(file)}
                                  disabled={deletingMarkedFile === file.id}
                                  className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 transition-colors disabled:opacity-50 text-xs font-medium"
                                  title="ลบทันที"
                                >
                                  {deletingMarkedFile === file.id ? (
                                    <RefreshCw className="w-3 h-3 animate-spin" />
                                  ) : (
                                    'ลบทันที'
                                  )}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="mt-4 bg-white rounded-lg p-3">
                  <p className="text-xs text-gray-600">
                    <strong>หมายเหตุ:</strong> ไฟล์เหล่านี้จะถูกลบอัตโนมัติหลังจากครบ 30 วัน นับจากวันที่บทเรียนถูกลบ 
                    คุณสามารถกู้คืนไฟล์หรือลบทันทีได้ก่อนหมดเวลา
                  </p>
                </div>
              </div>
            )}

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <HardDrive className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-blue-900 mb-1">ข้อมูล Storage</p>
                  <p className="text-sm text-blue-700">
                    ระบบใช้ <strong>Cloudflare R2</strong> สำหรับจัดเก็บข้อมูลทั้งหมด
                    ซึ่งมีความเร็วสูงและค่าใช้จ่ายต่ำ โดยไม่มีค่า Egress (ดาวน์โหลด)
                  </p>
                  <p className="text-xs text-blue-600 mt-2">
                    💡 ข้อมูลที่แสดงคำนวณจาก Firestore metadata ซึ่งอาจแตกต่างจากข้อมูลจริงใน R2 เล็กน้อย
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Cleanup Modal */}
      {showCleanupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-textDark flex items-center gap-2">
                  <AlertTriangle className="w-6 h-6 text-blue-600" />
                  ทำความสะอาดไฟล์ Orphaned
                </h3>
                <button
                  onClick={() => setShowCleanupModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <strong>คำเตือน:</strong> ฟีเจอร์นี้จะตรวจสอบและลบ metadata ของไฟล์ที่:
                </p>
                <ul className="list-disc list-inside text-sm text-yellow-700 mt-2 space-y-1">
                  <li>ไม่มี URL หรือ key</li>
                  <li>วิดีโอที่ไม่ได้ถูกอ้างอิงโดย lesson ใดๆ</li>
                  <li>คอร์สหลักถูกลบไปแล้ว</li>
                  <li>ผู้อัปโหลดถูกลบบัญชีแล้ว</li>
                </ul>
              </div>

              <div className="space-y-3">
                <button
                  onClick={async () => {
                    await handleCleanOrphanedMedia(true);
                  }}
                  disabled={loadingCleanup}
                  className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <Eye className="w-5 h-5" />
                  {loadingCleanup ? 'กำลังตรวจสอบ...' : 'ตรวจสอบเท่านั้น (Dry Run)'}
                </button>

                <button
                  onClick={async () => {
                    if (confirm('คุณแน่ใจหรือไม่ว่าต้องการลบไฟล์ orphaned? การกระทำนี้ไม่สามารถย้อนกลับได้!')) {
                      await handleCleanOrphanedMedia(false);
                      setShowCleanupModal(false);
                    }
                  }}
                  disabled={loadingCleanup}
                  className="w-full px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <Trash2 className="w-5 h-5" />
                  {loadingCleanup ? 'กำลังลบ...' : 'ลบไฟล์ Orphaned (ถาวร)'}
                </button>

                <button
                  onClick={() => setShowCleanupModal(false)}
                  className="w-full px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
                >
                  ยกเลิก
                </button>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-xs text-blue-800">
                  <strong>หมายเหตุ:</strong> แนะนำให้ใช้ "ตรวจสอบเท่านั้น" ก่อนเพื่อดูรายการไฟล์ที่จะถูกลบ
                  จากนั้นจึงทำการลบจริง
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recalculate Storage Modal */}
      {showRecalculateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-textDark flex items-center gap-2">
                  <RefreshCw className="w-6 h-6 text-green-600" />
                  กู้คืนข้อมูล Storage
                </h3>
                <button
                  onClick={() => setShowRecalculateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800">
                  <strong>ฟีเจอร์นี้จะ:</strong>
                </p>
                <ul className="list-disc list-inside text-sm text-green-700 mt-2 space-y-1">
                  <li>คำนวณพื้นที่ storage ที่ใช้จริงจากไฟล์ทั้งหมด</li>
                  <li>อัปเดทค่า storageUsedBytes ให้ตรงกับความเป็นจริง</li>
                  <li>แก้ไขปัญหาพื้นที่ที่คำนวณผิดพลาด</li>
                </ul>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <strong>⚠️ คำเตือน:</strong> การคำนวณใหม่จะใช้เวลานาน (ประมาณ 1-5 นาที) 
                  ขึ้นอยู่กับจำนวนผู้ใช้และไฟล์ในระบบ
                </p>
              </div>

              <div className="space-y-3">
                <button
                  onClick={async () => {
                    if (confirm('คุณแน่ใจหรือไม่ว่าต้องการคำนวณพื้นที่ storage ใหม่สำหรับผู้ใช้ทั้งหมด?')) {
                      await handleRecalculateStorage();
                      setShowRecalculateModal(false);
                    }
                  }}
                  disabled={loadingRecalculate}
                  className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <RefreshCw className="w-5 h-5" />
                  {loadingRecalculate ? 'กำลังคำนวณ...' : 'คำนวณใหม่ทั้งหมด'}
                </button>

                <button
                  onClick={() => setShowRecalculateModal(false)}
                  className="w-full px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
                >
                  ยกเลิก
                </button>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-xs text-blue-800">
                  <strong>💡 เคล็ดลับ:</strong> ใช้ฟีเจอร์นี้เมื่อ:
                </p>
                <ul className="list-disc list-inside text-xs text-blue-700 mt-1 space-y-1">
                  <li>พบว่าพื้นที่ storage แสดงผลผิดพลาด</li>
                  <li>หลังจากลบไฟล์ orphaned</li>
                  <li>หลังจากย้ายข้อมูลหรือแก้ไขระบบ</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderSystemSettings = () => <AdminSettings />;

  const renderMaintenanceMode = () => (
    <div className="bg-white rounded-2xl shadow-xl">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-red-600 rounded-xl flex items-center justify-center">
            <Wrench className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-textDark">โหมดบำรุงระบบ</h2>
            <p className="text-sm text-textLight">จัดการโหมดปรับปรุงระบบ</p>
          </div>
        </div>
      </div>
      
      <div className="p-6">
        {maintenanceSuccessMessage && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <Save className="w-5 h-5 text-green-600" />
            </div>
            <p className="text-green-800 font-semibold">{maintenanceSuccessMessage}</p>
          </div>
        )}

        <div className="mb-6 p-6 bg-gray-50 rounded-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${maintenanceEnabled ? "bg-red-100" : "bg-green-100"}`}>
                {maintenanceEnabled ? (
                  <PowerOff className="w-8 h-8 text-red-600" />
                ) : (
                  <Power className="w-8 h-8 text-green-600" />
                )}
              </div>
              <div>
                <h3 className="text-xl font-bold text-textDark">สถานะปัจจุบัน</h3>
                <p className={`text-lg font-semibold ${maintenanceEnabled ? "text-red-600" : "text-green-600"}`}>
                  {maintenanceEnabled ? "🔴 กำลังปรับปรุง" : "🟢 เปิดให้บริการปกติ"}
                </p>
              </div>
            </div>
            
            <button
              onClick={toggleMaintenance}
              disabled={loadingMaintenance}
              className={`px-8 py-4 rounded-xl font-bold text-lg transition-all ${maintenanceEnabled ? "bg-green-500 hover:bg-green-600 text-white" : "bg-red-500 hover:bg-red-600 text-white"} disabled:opacity-50`}
            >
              {maintenanceEnabled ? "เปิดให้บริการ" : "ปิดปรับปรุง"}
            </button>
          </div>
        </div>

        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 text-blue-600 mt-0.5" />
          <div className="flex-1">
            <p className="text-blue-900 font-semibold mb-1">ข้อมูลสำคัญ</p>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• เมื่อเปิดโหมดปรับปรุง ผู้ใช้ทั่วไปจะไม่สามารถเข้าใช้งานเว็บได้</li>
              <li>• Admin ยังสามารถเข้าใช้งานได้ตามปกติ</li>
              <li>• ข้อความที่ตั้งค่าจะแสดงบนหน้า Maintenance</li>
              <li>• การเปลี่ยนแปลงจะมีผลทันที</li>
            </ul>
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-lg font-bold text-textDark mb-2">ข้อความแจ้งผู้ใช้</label>
          <p className="text-sm text-textLight mb-4">ข้อความนี้จะแสดงบนหน้า Maintenance เมื่อเปิดโหมดปรับปรุง</p>
          <textarea
            value={maintenanceMessage}
            onChange={(e) => setMaintenanceMessage(e.target.value)}
            rows={6}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none text-textDark"
            placeholder="ใส่ข้อความที่ต้องการแจ้งผู้ใช้..."
          />
        </div>

        <button
          onClick={handleSaveMaintenance}
          disabled={savingMaintenance}
          className="w-full bg-primary hover:bg-primary/90 text-white font-bold py-4 rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50"
        >
          <Save className="w-5 h-5" />
          {savingMaintenance ? "กำลังบันทึก..." : "บันทึกข้อความ"}
        </button>

        <div className="mt-6">
          <h3 className="text-lg font-bold text-textDark mb-4">ตัวอย่างหน้า Maintenance</h3>
          <div className="bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 rounded-xl p-8">
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-md mx-auto">
              <div className="flex justify-center mb-6">
                <div className="bg-blue-100 p-5 rounded-full animate-pulse">
                  <Wrench className="w-12 h-12 text-blue-600" />
                </div>
              </div>
              <div className="text-gray-900 whitespace-pre-line">{maintenanceMessage}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSystemHealth = () => {
    const getStatusColor = (status: string) => {
      switch (status) {
        case 'operational': return { border: 'border-green-200', bg: 'bg-green-50', icon: 'text-green-600', Icon: CheckCircle };
        case 'warning': return { border: 'border-yellow-200', bg: 'bg-yellow-50', icon: 'text-yellow-600', Icon: AlertCircle };
        case 'error': return { border: 'border-red-200', bg: 'bg-red-50', icon: 'text-red-600', Icon: XCircle };
        default: return { border: 'border-gray-200', bg: 'bg-gray-50', icon: 'text-gray-600', Icon: CheckCircle };
      }
    };

    return (
      <div className="bg-white rounded-2xl shadow-xl">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-textDark">สถานะระบบ</h2>
                <p className="text-sm text-textLight">ตรวจสอบสถานเบริการ</p>
              </div>
            </div>
            <button
              onClick={loadSystemHealth}
              disabled={loadingSystemHealth}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-semibold disabled:opacity-50 flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loadingSystemHealth ? 'animate-spin' : ''}`} />
              {loadingSystemHealth ? 'กำลังตรวจสอบ...' : 'ตรวจสอบ'}
            </button>
          </div>
        </div>
        <div className="p-6">
          {loadingSystemHealth ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
              <p className="text-gray-500">กำลังตรวจสอบระบบ...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(systemHealth).map(([key, value]: [string, any]) => {
                const colors = getStatusColor(value.status);
                const StatusIcon = colors.Icon;
                const serviceName = key === 'firebase' ? 'Firebase' :
                                   key === 'cloudflareR2' ? 'Cloudflare R2' :
                                   key === 'authentication' ? 'Authentication' :
                                   key === 'database' ? 'Database' : key;
                
                return (
                  <div key={key} className={`border ${colors.border} ${colors.bg} rounded-lg p-4`}>
                    <div className="flex items-center gap-3 mb-2">
                      <StatusIcon className={`w-5 h-5 ${colors.icon}`} />
                      <p className="font-semibold text-gray-900">{serviceName}</p>
                    </div>
                    <p className="text-sm text-gray-600">{value.message}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderNotifications = () => (
    <div className="bg-white rounded-2xl shadow-xl">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-cyan-600 rounded-xl flex items-center justify-center">
              <Bell className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-textDark">การแจ้งเตือน</h2>
              <p className="text-sm text-textLight">การแจ้งเตือนของระบบ</p>
            </div>
          </div>
          <button
            onClick={loadNotifications}
            disabled={loadingNotifications}
            className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors text-sm font-semibold disabled:opacity-50 flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loadingNotifications ? 'animate-spin' : ''}`} />
            {loadingNotifications ? 'กำลังโหลด...' : 'รีเฟรช'}
          </button>
        </div>
      </div>
      <div className="p-6">
        {loadingNotifications ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-600 mx-auto mb-4"></div>
            <p className="text-gray-500">กำลังโหลดข้อมูล...</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-12">
            <Bell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">ไม่มีการแจ้งเตือนใหม่</p>
            <p className="text-xs text-gray-400 mt-2">การแจ้งเตือนจะแสดงที่นี่เมื่อมีข้อความสำคัญจากระบบ</p>
          </div>
        ) : (
          <div className="space-y-3">
            {notifications.map((notif) => (
              <div
                key={notif.id}
                className={`p-4 rounded-lg border-l-4 ${
                  notif.type === 'error' ? 'bg-red-50 border-red-500' :
                  notif.type === 'warning' ? 'bg-yellow-50 border-yellow-500' :
                  notif.type === 'success' ? 'bg-green-50 border-green-500' :
                  'bg-blue-50 border-blue-500'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        notif.type === 'error' ? 'bg-red-100 text-red-800' :
                        notif.type === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                        notif.type === 'success' ? 'bg-green-100 text-green-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {notif.type === 'error' ? 'ข้อผิดพลาด' :
                         notif.type === 'warning' ? 'คำเตือน' :
                         notif.type === 'success' ? 'สำเร็จ' : 'ข้อมูล'}
                      </span>
                      <p className="text-sm font-semibold text-gray-900">{notif.title}</p>
                    </div>
                    <p className="text-sm text-gray-700">{notif.message || notif.description}</p>
                    <p className="text-xs text-gray-500 mt-2">
                      {notif.createdAt?.toDate?.()?.toLocaleString('th-TH') || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderAnalytics = () => (
    <div className="bg-white rounded-2xl shadow-xl">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-textDark">วิเคราะห์ข้อมูล</h2>
              <p className="text-sm text-textLight">สถิติและการวิเคราะห์</p>
            </div>
          </div>
          <button
            onClick={loadAnalytics}
            disabled={loadingAnalytics}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-semibold disabled:opacity-50 flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loadingAnalytics ? 'animate-spin' : ''}`} />
            {loadingAnalytics ? 'กำลังโหลด...' : 'รีเฟรช'}
          </button>
        </div>
      </div>
      <div className="p-6">
        {loadingAnalytics ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-500">กำลังโหลดข้อมูล...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-1">ผู้ใช้ใหม่ (7 วัน)</p>
              <p className="text-2xl font-bold text-blue-600">{analyticsData.newUsers7Days}</p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-1">คอร์สใหม่ (7 วัน)</p>
              <p className="text-2xl font-bold text-green-600">{analyticsData.newCourses7Days}</p>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-1">การเข้าชม (วันนี้)</p>
              <p className="text-2xl font-bold text-blue-600">{analyticsData.visitsToday}</p>
            </div>
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-1">อัตราการเติบโต</p>
              <p className="text-2xl font-bold text-blue-600">{analyticsData.growthRate}%</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderUsageStats = () => (
    <div className="bg-white rounded-2xl shadow-xl">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-pink-600 rounded-xl flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-textDark">สถิติการใช้งาน</h2>
              <p className="text-sm text-textLight">ข้อมูลการใช้งานระบบ</p>
            </div>
          </div>
          <button
            onClick={loadUsageStats}
            disabled={loadingUsageStats}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-semibold disabled:opacity-50 flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loadingUsageStats ? 'animate-spin' : ''}`} />
            {loadingUsageStats ? 'กำลังโหลด...' : 'รีเฟรช'}
          </button>
        </div>
      </div>
      <div className="p-6">
        {loadingUsageStats ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-500">กำลังโหลดข้อมูล...</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <p className="font-medium text-gray-900">ผู้ใช้ที่ Active</p>
                <p className="text-2xl font-bold text-blue-600">{usageStats.activeUsers}</p>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-blue-600 h-2 rounded-full" style={{width: usageStats.activeUsers > 0 ? '100%' : '0%'}}></div>
              </div>
            </div>
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <p className="font-medium text-gray-900">คอร์สที่เปิดใช้งาน</p>
                <p className="text-2xl font-bold text-blue-600">{usageStats.activeCourses}</p>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-blue-600 h-2 rounded-full" style={{width: usageStats.activeCourses > 0 ? '100%' : '0%'}}></div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderPartners = () => (
    <div className="bg-white rounded-2xl shadow-xl">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-pink-600 rounded-xl flex items-center justify-center">
              <Handshake className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-textDark">จัดการพาร์ทเนอร์</h2>
              <p className="text-sm text-textLight">จัดการพาร์ทเนอร์ที่แสดงในหน้าแรก</p>
            </div>
          </div>
          <Link to="/admin/partners">
            <button className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-pink-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all">
              <ExternalLink className="w-5 h-5" />
              จัดการเต็มรูปแบบ
            </button>
          </Link>
        </div>
      </div>
      
      <div className="p-6">
        {loadingPartners ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-500">กำลังโหลดข้อมูล...</p>
          </div>
        ) : partners.length === 0 ? (
          <div className="text-center py-12">
            <Star className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">ยังไม่มีพาร์ทเนอร์ในระบบ</p>
            <Link to="/admin/partners">
              <button className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                <Plus className="w-5 h-5" />
                เพิ่มพาร์ทเนอร์
              </button>
            </Link>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {partners.slice(0, 6).map((partner: any) => (
                <div
                  key={partner.id}
                  className={`border-2 rounded-xl p-4 ${
                    partner.isActive ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-start gap-3 mb-3">
                    {partner.instructorImage ? (
                      <img
                        src={partner.instructorImage}
                        alt={partner.instructorName}
                        className="w-16 h-16 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-400 to-pink-400 flex items-center justify-center text-2xl font-bold text-white">
                        {partner.instructorName?.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1">
                      <h3 className="font-bold text-textDark">{partner.instructorName}</h3>
                      <p className="text-xs text-textLight line-clamp-2">{partner.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-600">
                    <span>📚 {partner.coursesCount || 0} คอร์ส</span>
                    <span>👥 {partner.studentsCount || 0} คน</span>
                    <span className={`ml-auto px-2 py-1 rounded-full text-xs font-semibold ${
                      partner.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700'
                    }`}>
                      {partner.isActive ? '✓ แสดง' : '✕ ซ่อน'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-600 mb-4">
                แสดง {Math.min(6, partners.length)} จาก {partners.length} พาร์ทเนอร์
              </p>
              <Link to="/admin/partners">
                <button className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                  <Handshake className="w-5 h-5" />
                  จัดการพาร์ทเนอร์ทั้งหมด
                </button>
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );

  const renderActivityLogs = () => (
    <div className="bg-white rounded-2xl shadow-xl">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-red-600 rounded-xl flex items-center justify-center">
              <Activity className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-textDark">บันทึกกิจกรรม</h2>
              <p className="text-sm text-textLight">ประวัติการใช้งาน</p>
            </div>
          </div>
          <button
            onClick={loadActivityLogs}
            disabled={loadingActivityLogs}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-semibold disabled:opacity-50 flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loadingActivityLogs ? 'animate-spin' : ''}`} />
            {loadingActivityLogs ? 'กำลังโหลด...' : 'รีเฟรช'}
          </button>
        </div>
      </div>
      
      {loadingActivityLogs ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">กำลังโหลดข้อมูล...</p>
        </div>
      ) : activityLogs.length === 0 ? (
        <div className="text-center py-12">
          <Activity className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">ยังไม่มีบันทึกกิจกรรม</p>
          <p className="text-xs text-gray-400 mt-2">บันทึกกิจกรรมจะแสดงที่นี่เมื่อมีการใช้งานระบบ</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">เวลา</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ผู้ใช้</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">กิจกรรม</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">รายละเอียด</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {activityLogs.map((log, index) => (
                <tr key={log.id || index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {log.timestamp?.toDate?.()?.toLocaleString('th-TH') || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {log.userName || log.userEmail || log.userId || 'Unknown'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      log.type === 'error' ? 'bg-red-100 text-red-800' :
                      log.type === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                      log.type === 'success' ? 'bg-green-100 text-green-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {log.action}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">{log.details || log.description || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderPaymentSettings = () => <PaymentSettingsSection />;

  const renderAISystem = () => <AISystemAdmin />;

  // Render content based on active section
  switch (activeSection) {
    case 'overview':
      return renderOverview();
    case 'all-users':
      return renderAllUsers();
    case 'instructors':
      return renderInstructors();
    case 'partners':
      return renderPartners();
    case 'students':
      window.location.href = '/admin/system';
      return null;
    case 'all-courses':
      return renderAllCourses();
    case 'flagged-courses':
      return renderFlaggedCourses();
    case 'total-revenue':
      return renderTotalRevenue();
    case 'subscription-payments':
      return renderSubscriptionPayments();
    case 'payment-approval':
      return renderPaymentApproval();
    case 'payment-settings':
      return renderPaymentSettings();
    case 'subscription-management':
      return renderSubscriptionManagement();
    case 'discount-management':
      return renderDiscountManagement();
    case 'storage-management':
      return renderStorageManagement();
    case 'system-settings':
      return renderSystemSettings();
    case 'maintenance-mode':
      return renderMaintenanceMode();
    case 'video-reports':
      return <VideoReportsAdmin />;
    case 'activity-logs':
      return renderActivityLogs();
    case 'system-health':
      return renderSystemHealth();
    case 'notifications':
      return renderNotifications();
    case 'analytics':
      return renderAnalytics();
    case 'usage-stats':
      return renderUsageStats();
    case 'ai-system':
      return renderAISystem();
    default:
      return renderOverview();
  }
}
